/**
 * @fileoverview Gerenciamento de Empresas (groupName) → Contas (Company) → Usuários.
 *
 * Fluxo: superadmin lista Empresas (grupos) como cards; ao clicar abre detalhe
 * com 2 seções: Contas (Companies) e Usuários (todos os users do grupo).
 * Multi-conta de usuário via UserCompanies.
 */
import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Tooltip, Menu, MenuItem, Divider } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Building2, Users, Plus, Pencil, Trash2, ChevronRight, ChevronDown, X, MessageCircle, Headphones, Sparkles, ShieldCheck, ShieldOff, RotateCcw, Search, Eye, EyeOff, Check, LogIn, Settings, Ban, LayoutGrid, List, MoreVertical, ArrowRight, ArrowUpDown } from "lucide-react";
import ConfirmationModal from "../ConfirmationModal";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import UsersTable, { UsersTablePagination, initials, nextUsersSort, sortUsers } from "../UsersTable";
import AccountSelectorModal from "./AccountSelectorModal";
import UserDeleteImpactModal from "../UserDeleteImpactModal";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useClickOutside } from "../../hooks/useClickOutside";
import AccountsPicker from "./AccountsPicker";
import { LimitMeter } from "./meters";
import CompanyPlanModal from "./CompanyPlanModal";
import stripAccents from "../../utils/stripAccents";

const ACCESS_SCOPES = [
  { value: "full", label: "Completo (Chat + Atendimento)", Icon: Sparkles, color: "#5B7CFF" },
  { value: "internal", label: "Apenas Chat Interno", Icon: MessageCircle, color: "#10B981" },
  { value: "attendance", label: "Apenas Atendimento", Icon: Headphones, color: "#F59E0B" },
];

// Máscara progressiva de CNPJ (00.000.000/0000-00). Armazenamos só dígitos.
const maskCnpj = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  // Tokens do tema CLARO (o escuro não muda). Fundo da página levemente
  // acinzentado + bordas/divisórias mais firmes + sombra sutil nos cards,
  // pra card branco não sumir sobre fundo claro.
  const L = {
    pageBg: "#eef0f4",
    border: "#d8dde6",
    divider: "#e2e6ec",
    track: "#e2e6ec",
    muted: "#64748b",
    shadow: "0 1px 2px rgba(15,23,42,.06), 0 1px 3px rgba(15,23,42,.08)",
  };
  const cardShadow = dark ? "none" : L.shadow;
  return {
    // Layout de página: o scroll vertical é controlado pelo MainContainer
    // pai. Sem maxHeight/overflowY aqui — evita scroll duplo e mantém o shell
    // atmosférico do tema dark visível atrás do conteúdo.
    //
    // paddingBottom + spacer-div (.bottomSpacer) garantem respiro premium.
    // Por que os dois: padding-bottom em flex column tem quirk em alguns
    // browsers — quando o conteúdo ultrapassa a viewport e entra em scroll,
    // o padding pode ser ignorado no extremo do scroll. O spacer-div força
    // a altura real e é à prova de quirks.
    root: {
      padding: theme.spacing(4),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(14),
      minHeight: "100%",
      // Filho de um contêiner flex que rola (MainContainer): sem isto, encolhia até a
      // altura da tela e o fundo cinza acabava ali, com o resto da página em branco.
      flexShrink: 0,
      background: dark ? "transparent" : L.pageBg,
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5),
        paddingBottom: theme.spacing(10),
      },
    },
    bottomSpacer: {
      height: theme.spacing(12),
      flexShrink: 0,
      [theme.breakpoints.down("sm")]: { height: theme.spacing(8) },
    },
    listHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      gap: 16,
      marginBottom: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : L.divider}`,
      flexWrap: "wrap",
    },
    listTitle: { fontSize: 26, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", letterSpacing: "-0.6px", lineHeight: 1.15 },
    listSub: { fontSize: 13, color: dark ? "#A1A1AA" : L.muted, marginTop: 6, fontVariantNumeric: "tabular-nums" },
    // Estilos espelham `actionBtnBase` / `actionBtnPrimary` / `actionBtnSecondary`
    // da página /Contacts — fonte do padrão de botões secundários da plataforma.
    // ── Botões: alinhados ao padrão TicketsManagerTabs filter dialog ──
    // primary = gradient azul-violeta, ghost = transparente com texto cinza,
    // danger = gradient vermelho. textTransform "none" e radius 12.
    primaryBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 36,
      minWidth: 0,
      padding: "0 14px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      border: "none",
      fontFamily: "inherit",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    ghostBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 38,
      minWidth: 0,
      padding: "0 18px",
      borderRadius: 12,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background 180ms, border-color 180ms, color 180ms",
      background: "transparent",
      color: dark ? "#94a3b8" : "#6b7280",
      border: `1px solid ${dark ? "rgba(255, 255, 255, 0.10)" : "rgba(15, 23, 42, 0.10)"}`,
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
        borderColor: dark ? "rgba(91, 124, 255, 0.30)" : "rgba(61, 92, 253, 0.28)",
        color: dark ? "#e2e8f0" : "#374151",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    dangerBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 38,
      minWidth: 0,
      padding: "0 18px",
      borderRadius: 12,
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      border: "none",
      fontFamily: "inherit",
      transition: "background 180ms, box-shadow 180ms",
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      color: "#ffffff",
      boxShadow: "0 4px 12px rgba(220,38,38,0.20)",
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
        boxShadow: "0 6px 16px rgba(220,38,38,0.30)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },
    },
    cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
    iconBtn: { width: 32, height: 32, border: "none", background: "transparent", borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: dark ? "#A1A1AA" : "#71717A", transition: "background 0.15s, color 0.15s", "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" } },
    danger: { "&:hover": { background: "rgba(239,68,68,0.1)", color: "#EF4444" } },
    accountsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 },
    accountCard: { padding: 16, borderRadius: 12, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : L.border}`, background: dark ? "rgba(255,255,255,0.02)" : "#fff", boxShadow: cardShadow },
    // Conta desativada (soft-disable): borda/fundo avermelhados + conteúdo esmaecido.
    accountCardDisabled: { borderColor: "rgba(239,68,68,0.35)", background: dark ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.03)", "& $accountName, & $accountStats": { opacity: 0.55 } },
    accountHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 },
    accountDisabledBadge: { display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: "rgba(239,68,68,0.12)", color: "#EF4444", flexShrink: 0 },
    accountName: { fontSize: 15, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", display: "flex", alignItems: "center", gap: 8 },
    accountStats: { display: "flex", flexDirection: "column", gap: 4, marginTop: 10, fontSize: 12, color: dark ? "#A1A1AA" : "#71717A" },
    accountActions: { display: "flex", gap: 4, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : L.divider}`, justifyContent: "flex-end" },
    emptyState: { textAlign: "center", padding: 48, color: dark ? "#71717A" : "#A1A1AA", fontSize: 14, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 12, border: `1px dashed ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` },
    // ── Empty state premium para a listagem inicial ──
    emptyHero: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "72px 24px",
      borderRadius: 16,
      border: `1px dashed ${dark ? "rgba(255,255,255,0.08)" : L.border}`,
      background: dark
        ? "linear-gradient(180deg, rgba(91,124,255,0.04) 0%, rgba(255,255,255,0.01) 100%)"
        : "#fff",
    },
    emptyHeroIcon: {
      width: 64,
      height: 64,
      borderRadius: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 18,
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      boxShadow: dark
        ? "0 0 0 1px rgba(91,124,255,0.18), 0 8px 24px rgba(91,124,255,0.10)"
        : "0 0 0 1px rgba(61,92,253,0.12), 0 8px 24px rgba(61,92,253,0.08)",
    },
    emptyHeroTitle: { fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", letterSpacing: "-0.3px" },
    emptyHeroSub: { fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginTop: 6, maxWidth: 360, lineHeight: 1.55 },
    emptyHeroCta: { marginTop: 22 },
    // ── Skeleton de carregamento ──
    skeletonCard: {
      padding: 20,
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : L.border}`,
      background: dark ? "rgba(255,255,255,0.02)" : "#fff",
      boxShadow: cardShadow,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: dark
          ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)"
          : "linear-gradient(90deg, transparent 0%, rgba(15,23,42,0.04) 50%, transparent 100%)",
        animation: "$shimmer 1.4s ease-in-out infinite",
      },
    },
    skeletonRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
    skeletonBlock: {
      borderRadius: 8,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)",
    },
    skeletonFooter: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, paddingTop: 16, borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}` },
    "@keyframes shimmer": {
      "0%": { transform: "translateX(-100%)" },
      "100%": { transform: "translateX(100%)" },
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Listagem de Empresas — toolbar (busca / ordenação / visão)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    // Ordenação — usa o SingleSelect (dropdown padrão do projeto) com trigger
    // compacto na altura da toolbar.
    toolbarSortWrap: { width: 240, flexShrink: 0 },
    toolbarSortTrigger: {
      height: 40,
      padding: "0 12px",
      fontSize: 13,
      borderColor: dark ? "rgba(255,255,255,0.10)" : L.border,
      "& svg:first-child": { color: dark ? "#71717A" : L.muted, flexShrink: 0 },
    },
    // Toggle cards ⇄ lista (segmented control). Escondido no mobile.
    viewToggle: {
      display: "inline-flex",
      alignItems: "center",
      height: 40,
      padding: 3,
      borderRadius: 10,
      background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.04)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : L.border}`,
      flexShrink: 0,
      marginLeft: "auto",
    },
    viewToggleBtn: {
      height: 32,
      minWidth: 36,
      padding: "0 10px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: dark ? "#A1A1AA" : L.muted,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .15s, color .15s, box-shadow .15s",
      "&:hover": { color: dark ? "#FAFAFA" : "#09090B" },
    },
    viewToggleBtnActive: {
      background: dark ? "rgba(255,255,255,0.09)" : "#fff",
      color: dark ? "#FAFAFA" : "#09090B",
      boxShadow: dark ? "none" : "0 1px 2px rgba(15,23,42,0.10)",
    },
    resultsLine: {
      fontSize: 13,
      color: dark ? "#A1A1AA" : L.muted,
      marginBottom: 14,
      fontVariantNumeric: "tabular-nums",
      "& strong": { color: dark ? "#FAFAFA" : "#09090B", fontWeight: 600 },
    },
    emptySearch: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "56px 24px",
      borderRadius: 14,
      border: `1px dashed ${dark ? "rgba(255,255,255,0.10)" : L.border}`,
      background: dark ? "rgba(255,255,255,0.015)" : "#fff",
    },
    emptySearchIcon: {
      width: 48, height: 48, borderRadius: 14,
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 14,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dark ? "#A1A1AA" : L.muted,
    },
    emptySearchTitle: { fontSize: 15, fontWeight: 600, color: dark ? "#FAFAFA" : "#09090B" },
    emptySearchSub: { fontSize: 13, color: dark ? "#A1A1AA" : L.muted, marginTop: 4 },
    emptySearchBtn: { marginTop: 16 },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Listagem de Empresas — grade de cards (largura uniforme, 300px+)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    trackingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    trackingCard: {
      padding: 18,
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.07)" : L.border}`,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      boxShadow: cardShadow,
      cursor: "pointer",
      transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      minWidth: 0,
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.28)",
        boxShadow: dark
          ? "0 10px 28px rgba(91,124,255,0.08)"
          : "0 10px 28px rgba(61,92,253,0.06)",
        transform: "translateY(-1px)",
      },
      "&:focus-visible": {
        outline: "none",
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark ? "0 0 0 3px rgba(91,124,255,0.20)" : "0 0 0 3px rgba(61,92,253,0.16)",
      },
    },
    trackingCardHeader: { display: "flex", alignItems: "flex-start", gap: 12 },
    // Logo/inicial da Empresa — tom neutro (sem arco-íris), inicial em destaque.
    trackingIconBox: {
      width: 42,
      height: 42,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: dark ? "#FAFAFA" : "#1F2937",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : L.border}`,
      fontSize: 17,
      fontWeight: 700,
      letterSpacing: "-0.3px",
      textTransform: "uppercase",
      userSelect: "none",
    },
    trackingIconBoxSm: {
      width: 34,
      height: 34,
      borderRadius: 9,
      fontSize: 14,
    },
    trackingCardTitle: { flex: 1, minWidth: 0 },
    trackingCardName: {
      fontSize: 16,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.3px",
      lineHeight: 1.25,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    trackingBadges: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, "&:empty": { display: "none" } },
    trackingSystemBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: 6,
      background: dark ? "rgba(251,191,36,0.10)" : "#fdf0d2",
      color: dark ? "#fbbf24" : "#a16207",
      border: `1px solid ${dark ? "rgba(251,191,36,0.30)" : "#f0d48a"}`,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.2,
      lineHeight: "16px",
    },
    // Empresa (grupo) com contas desativadas — badge vermelho, mesmo molde do "Sistema".
    trackingDisabledBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "2px 8px",
      borderRadius: 6,
      background: "rgba(239,68,68,0.10)",
      color: "#EF4444",
      border: "1px solid rgba(239,68,68,0.30)",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.2,
      lineHeight: "16px",
    },
    // Todas as contas do grupo desativadas → card inteiro com visual bloqueado.
    trackingCardDisabled: {
      borderColor: "rgba(239,68,68,0.35)",
      background: dark ? "rgba(239,68,68,0.05)" : "rgba(239,68,68,0.03)",
      "& $trackingCardName, & $meterList": { opacity: 0.55 },
      "& $trackingIconBox": {
        background: "rgba(239,68,68,0.10)",
        color: "#EF4444",
        border: "1px solid rgba(239,68,68,0.25)",
      },
      "&:hover": {
        borderColor: "rgba(239,68,68,0.50)",
        boxShadow: "0 10px 28px rgba(239,68,68,0.08)",
        transform: "translateY(-1px)",
      },
    },

    // ── Medidores de limite (barra + semáforo) ──
    meterList: { display: "flex", flexDirection: "column", gap: 12 },
    meter: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    meterHead: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 8,
      fontSize: 12,
    },
    meterLabel: { color: dark ? "#A1A1AA" : L.muted, fontWeight: 500 },
    meterValue: {
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: dark ? "#FAFAFA" : "#09090B",
      whiteSpace: "nowrap",
      marginLeft: "auto",
    },
    meterValueMuted: { color: dark ? "#71717A" : L.muted, fontWeight: 500 },
    meterBarWrap: { minWidth: 0 },
    meterTrack: {
      height: 6,
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.08)" : L.track,
      overflow: "hidden",
    },
    meterTrackSm: { height: 4 },
    meterFill: {
      height: "100%",
      borderRadius: 999,
      transition: "width .3s ease",
    },
    meterOk: { "& $meterValue": { color: dark ? "#4ade80" : "#15803d" }, "& $meterFill": { background: dark ? "#22c55e" : "#16a34a" } },
    meterWarn: { "& $meterValue": { color: dark ? "#fbbf24" : "#b45309" }, "& $meterFill": { background: dark ? "#f59e0b" : "#d97706" } },
    meterFull: { "& $meterValue": { color: dark ? "#f87171" : "#dc2626" }, "& $meterFill": { background: "#ef4444" } },
    meterNone: { "& $meterFill": { background: dark ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.22)" } },

    // ── Menu ⋮ (dropdown irmão do botão, dentro de wrapper relative) ──
    menuWrap: { position: "relative", flexShrink: 0 },
    menuTrigger: {
      width: 32,
      height: 32,
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: dark ? "#A1A1AA" : L.muted,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      padding: 0,
      transition: "background .15s, color .15s",
      "&:hover, &[aria-expanded='true']": {
        background: dark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)",
        color: dark ? "#FAFAFA" : "#09090B",
      },
    },
    // Paper do <Menu> (MUI) — posicionado pelo Popover, não por nós.
    menuDropdown: {
      marginTop: 6,
      minWidth: 200,
      background: dark ? "#18181B" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : L.border}`,
      borderRadius: 10,
      boxShadow: dark ? "0 12px 32px rgba(0,0,0,0.5)" : "0 12px 32px rgba(15,23,42,0.14)",
      padding: 4,
      "& .MuiList-root": { padding: 0 },
    },
    // MenuItem do MUI com o visual do projeto (sobrescreve o ListItem padrão).
    menuItem: {
      minHeight: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 10px",
      borderRadius: 7,
      color: dark ? "#E4E4E7" : "#1F2937",
      fontSize: 13,
      fontWeight: 500,
      fontFamily: "inherit",
      lineHeight: 1.3,
      transition: "background .1s",
      "& svg": { flexShrink: 0, color: dark ? "#A1A1AA" : L.muted },
      "&:hover, &.Mui-focusVisible": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)" },
    },
    menuItemDanger: {
      color: dark ? "#fca5a5" : "#dc2626",
      "& svg": { color: "inherit" },
      "&:hover, &.Mui-focusVisible": { background: dark ? "rgba(239,68,68,0.10)" : "rgba(220,38,38,0.06)" },
    },
    menuDivider: {
      margin: "4px 6px",
      background: dark ? "rgba(255,255,255,0.08)" : L.divider,
    },

    // ── Rodapé do card: ações lado a lado ──
    trackingActionsRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
      gap: 8,
      marginTop: "auto",
    },
    trackingActionsRowSingle: {
      display: "grid",
      gridTemplateColumns: "1fr",
      marginTop: "auto",
    },
    trackingAccessBtn: {
      height: 38,
      padding: "0 10px",
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      transition: "transform .12s, opacity .15s, background .15s",
      fontFamily: "inherit",
      minWidth: 0,
      "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
      "&:active": { transform: "translateY(0)" },
      "&:disabled": { opacity: 0.7, cursor: "wait" },
    },
    trackingManageBtn: {
      height: 38,
      padding: "0 10px",
      borderRadius: 10,
      background: "transparent",
      border: `1px solid ${dark ? "rgba(255,255,255,0.14)" : "#c9d0db"}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      transition: "background .12s, border-color .12s",
      fontFamily: "inherit",
      minWidth: 0,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        borderColor: dark ? "rgba(255,255,255,0.24)" : "rgba(15,23,42,0.28)",
      },
    },
    // Variante compacta pra tabela (visão lista)
    btnSm: { height: 32, padding: "0 10px", fontSize: 12.5, borderRadius: 8 },
    trackingBtnLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    // Estado "Conta ativa" — verde sutil, cursor default. Quando a Empresa
    // tem múltiplas contas, vira clicável (abre seletor pra trocar) sem
    // perder o feedback visual de que já estamos dentro dela.
    trackingActiveBtn: {
      height: 38,
      padding: "0 10px",
      borderRadius: 10,
      background: dark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)",
      border: `1px solid ${dark ? "rgba(34,197,94,0.32)" : "rgba(34,197,94,0.22)"}`,
      color: dark ? "#86efac" : "#16a34a",
      fontSize: 13,
      fontWeight: 600,
      cursor: "default",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      fontFamily: "inherit",
      minWidth: 0,
      transition: "background .12s, border-color .12s",
    },
    trackingActiveBtnClickable: {
      cursor: "pointer",
      "&:hover": {
        background: dark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.12)",
        borderColor: dark ? "rgba(34,197,94,0.45)" : "rgba(34,197,94,0.32)",
      },
    },
    trackingActiveDot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#22c55e",
      boxShadow: "0 0 0 2px rgba(34,197,94,0.25)",
      flexShrink: 0,
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Listagem de Empresas — visão lista (tabela)
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    tableWrap: {
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.07)" : L.border}`,
      background: dark ? "rgba(255,255,255,0.02)" : "#fff",
      boxShadow: cardShadow,
      overflowX: "auto",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: 760,
      tableLayout: "fixed",
    },
    // Lista de Contas: 3 colunas cabem em telas médias sem rolar para o lado.
    tableNarrow: { minWidth: 520 },
    th: {
      textAlign: "left",
      padding: "11px 16px",
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: dark ? "#A1A1AA" : L.muted,
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.07)" : L.divider}`,
      background: dark ? "rgba(255,255,255,0.015)" : "#f5f6f9",
      whiteSpace: "nowrap",
      "&:first-child": { borderTopLeftRadius: 14 },
      "&:last-child": { borderTopRightRadius: 14 },
    },
    thRight: { textAlign: "right" },
    tr: {
      cursor: "pointer",
      transition: "background .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)" },
      "&:hover $td": { borderBottomColor: "transparent" },
      "&:last-child $td": { borderBottom: "none" },
    },
    trDisabled: {
      background: dark ? "rgba(239,68,68,0.04)" : "rgba(239,68,68,0.025)",
      "& $rowGroupName, & $meter": { opacity: 0.6 },
      "& $trackingIconBox": {
        background: "rgba(239,68,68,0.10)",
        color: "#EF4444",
        border: "1px solid rgba(239,68,68,0.25)",
      },
    },
    // Linha que não abre nada ao clicar (Contas: as ações ficam nos botões).
    trStatic: { cursor: "default" },
    td: {
      padding: "12px 16px",
      verticalAlign: "middle",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.05)" : L.divider}`,
      fontSize: 13,
      color: dark ? "#FAFAFA" : "#09090B",
    },
    tdNumber: { fontVariantNumeric: "tabular-nums" },
    rowGroup: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    rowGroupText: { minWidth: 0, flex: 1 },
    rowGroupName: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? "#FAFAFA" : "#09090B",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rowBadges: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 3, "&:empty": { display: "none" } },
    tableRowActions: { display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Detalhe — header novo + KPI cards
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    detailHeaderV2: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      marginBottom: 28,
      flexWrap: "wrap",
    },
    detailBackBtn: {
      width: 40,
      height: 40,
      borderRadius: 10,
      border: "none",
      background: "transparent",
      color: dark ? "#A1A1AA" : "#71717A",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background .15s, color .15s",
      flexShrink: 0,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
        color: dark ? "#FAFAFA" : "#09090B",
      },
    },
    detailTitleWrap: { flex: 1, minWidth: 0 },
    detailNameV2: {
      fontSize: 32,
      fontWeight: 800,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.8px",
      lineHeight: 1.1,
    },
    detailSubV2: { fontSize: 13, color: dark ? "#71717A" : L.muted, marginTop: 6 },
    detailActionsV2: { display: "flex", gap: 8, alignItems: "center", flexShrink: 0 },
    detailEditBtn: {
      height: 36,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.12)"}`,
      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .15s, border-color .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.04)",
        borderColor: dark ? "rgba(91,124,255,0.4)" : "rgba(61,92,253,0.32)",
      },
    },
    detailDeleteBtn: {
      height: 36,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(239,68,68,0.32)" : "rgba(220,38,38,0.28)"}`,
      background: "transparent",
      color: dark ? "#fca5a5" : "#dc2626",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .15s, border-color .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(239,68,68,0.08)" : "rgba(220,38,38,0.06)",
        borderColor: dark ? "rgba(239,68,68,0.5)" : "rgba(220,38,38,0.45)",
      },
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Section + Search
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    sectionV2: { marginBottom: 36 },
    sectionHeaderV2: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginBottom: 16,
      gap: 16,
      flexWrap: "wrap",
    },
    sectionTitleWrapV2: { display: "flex", alignItems: "baseline", gap: 10 },
    sectionTitleV2: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.4px",
    },
    sectionCountV2: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#71717A" : L.muted,
      fontVariantNumeric: "tabular-nums",
    },
    sectionActionsV2: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    // Busca — bloco único usado no detalhe (contas/usuários, 44px) e na
    // toolbar da lista (modificadores *Inline/*Sm, 40px).
    searchWrap: { position: "relative", marginBottom: 16 },
    searchWrapInline: { marginBottom: 0, flex: "1 1 260px", minWidth: 200, maxWidth: 420 },
    searchInput: {
      width: "100%",
      height: 44,
      paddingLeft: 44,
      paddingRight: 36,
      borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : L.border}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color .15s, background .15s, box-shadow .15s",
      "&:focus": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 1px rgba(91,124,255,0.15)"
          : "0 0 0 1px rgba(61,92,253,0.12)",
      },
      "&::placeholder": { color: dark ? "#71717A" : L.muted },
      // esconde o "x" nativo do type=search (temos o nosso: searchClear)
      "&::-webkit-search-cancel-button": { WebkitAppearance: "none", appearance: "none" },
    },
    searchInputSm: { height: 40, paddingLeft: 40, borderRadius: 10 },
    searchClear: {
      position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
      width: 26, height: 26, borderRadius: 7, border: "none", background: "transparent",
      color: dark ? "#A1A1AA" : L.muted, display: "inline-flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", padding: 0,
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)", color: dark ? "#FAFAFA" : "#09090B" },
    },
    searchIcon: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#71717A" : L.muted,
      pointerEvents: "none",
      display: "flex",
    },

    // ── Modal shell: alinhado ao TicketsManagerTabs filterDialog + QueueModal ──
    // Mesmo padrão visual usado no Chat de Atendimento: gradient dark suave,
    // border azul-violeta (#5B7CFF rgba 0.2), borderRadius 24, shadow profunda.
    // Light mode mantém o look limpo branco.
    modalBackdrop: {
      position: "fixed",
      inset: 0,
      background: dark ? "rgba(0,0,0,0.78)" : "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1300,
      padding: 16,
    },
    modalCard: {
      width: "100%",
      maxWidth: 520,
      background: dark
        ? "linear-gradient(135deg, rgba(18,18,19,0.98) 0%, rgba(22,22,24,0.98) 100%)"
        : "#ffffff",
      borderRadius: 24,
      border: `1px solid ${dark ? "rgba(91,124,255,0.20)" : "rgba(61,92,253,0.18)"}`,
      boxShadow: dark
        ? "0 20px 60px rgba(0,0,0,0.55)"
        : "0 20px 60px rgba(15,23,42,0.12)",
      overflow: "hidden",
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 22px 12px",
      borderBottom: `1px solid ${dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)"}`,
    },
    modalTitle: {
      fontSize: "1.125rem",
      fontWeight: 600,
      color: dark ? "#f1f5f9" : "#0f172a",
      letterSpacing: "-0.2px",
    },
    modalBody: { padding: "20px 22px", overflowY: "auto", flex: 1 },
    field: { marginBottom: 14 },
    fieldLabel: {
      display: "block",
      fontSize: 12,
      fontWeight: 500,
      color: dark ? "#94a3b8" : "#475569",
      marginBottom: 6,
      letterSpacing: 0.1,
    },
    // ── Field outlined: alinhado a QueueModal.fieldOutlined e ModernSelect ──
    // Mesma fórmula visual do sistema: background suave, border azul-violeta
    // discreta, focus com anel de 1px (0 0 0 1px rgba(91,124,255,0.15)) +
    // border highlight. Light mode mantém legibilidade clean.
    fieldInput: {
      width: "100%",
      // height fixa garante que email, password, text e select fiquem com a
      // mesma altura visual (browsers aplicam line-height/padding diferente
      // por type).
      height: 44,
      padding: "0 14px",
      background: dark ? "rgba(255,255,255,0.05)" : "#ffffff",
      border: `1px solid ${dark ? "rgba(91,124,255,0.15)" : "rgba(15,23,42,0.12)"}`,
      borderRadius: 14,
      color: dark ? "#f1f5f9" : "#0f172a",
      fontSize: 14,
      fontFamily: "inherit",
      lineHeight: 1.4,
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
      // Reset cross-browser
      WebkitAppearance: "none",
      MozAppearance: "none",
      appearance: "none",
      "&::placeholder": {
        color: dark ? "rgba(248,250,252,0.45)" : "#94a3b8",
        opacity: 1,
      },
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.22)",
      },
      "&:focus": {
        borderColor: dark ? "rgba(91,124,255,0.45)" : "rgba(61,92,253,0.40)",
        boxShadow: dark
          ? "0 0 0 1px rgba(91,124,255,0.15)"
          : "0 0 0 1px rgba(61,92,253,0.12)",
      },
      // Remove o "ring" amarelo/azul de autofill do Chrome
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
        WebkitBoxShadow: dark
          ? "0 0 0 1000px rgba(255,255,255,0.04) inset"
          : "0 0 0 1000px #ffffff inset",
        WebkitTextFillColor: dark ? "#FAFAFA" : "#09090B",
        caretColor: dark ? "#FAFAFA" : "#09090B",
        transition: "background-color 9999s ease-in-out 0s",
      },
    },
    fieldHint: { fontSize: 11, color: dark ? "#71717A" : "#A1A1AA", marginTop: 4 },
    fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: "12px 22px 16px",
      borderTop: `1px solid ${dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)"}`,
    },
    multiSelectWrap: { position: "relative" },
    multiSelectTrigger: {
      width: "100%", padding: "10px 14px",
      background: dark ? "rgba(255,255,255,0.04)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 10,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14, fontWeight: 500, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      transition: "border 0.15s",
      "&:hover": { borderColor: dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)" },
    },
    multiSelectLabel: { color: dark ? "#FAFAFA" : "#09090B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    multiSelectMenu: {
      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
      background: dark ? "#18181B" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 10,
      boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgb(0 0 0 / 15%)",
      zIndex: 1400,
      padding: 4,
      maxHeight: 240,
      overflowY: "auto",
    },
    multiSelectItem: {
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px", borderRadius: 8, cursor: "pointer",
      transition: "background 0.1s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" },
    },
    // Mesmo espaço dos campos do modal (field: 14px embaixo).
    mfaCard: {
      marginBottom: 14,
      padding: 14,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      background: dark ? "rgba(91, 124, 255, 0.06)" : "rgba(61, 92, 253, 0.04)",
      border: `1px solid ${dark ? "rgba(91, 124, 255, 0.18)" : "rgba(61, 92, 253, 0.12)"}`,
    },
    mfaCardLeft: {
      display: "flex", alignItems: "center", gap: 12, minWidth: 0,
    },
    mfaIconBadge: {
      width: 36, height: 36, borderRadius: 10,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    },
    mfaIconBadgeOn: {
      background: dark ? "rgba(16, 185, 129, 0.18)" : "rgba(16, 185, 129, 0.12)",
      color: dark ? "#34d399" : "#059669",
      border: `1px solid ${dark ? "rgba(16, 185, 129, 0.35)" : "rgba(16, 185, 129, 0.25)"}`,
    },
    mfaIconBadgeOff: {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
      color: dark ? "rgba(255, 255, 255, 0.45)" : "#94a3b8",
      border: `1px solid ${dark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    },
    mfaTextWrap: { display: "flex", flexDirection: "column", minWidth: 0 },
    mfaTitle: {
      fontSize: 13, fontWeight: 600,
      color: dark ? "#f1f5f9" : "#1e293b", lineHeight: 1.2,
    },
    mfaStatus: { fontSize: 11.5, fontWeight: 500, marginTop: 2 },
    mfaStatusOn: { color: dark ? "#34d399" : "#059669" },
    mfaStatusOff: { color: dark ? "rgba(255,255,255,0.5)" : "#64748b" },
    mfaResetBtn: {
      display: "inline-flex", alignItems: "center", gap: 6,
      height: 32, padding: "0 12px", borderRadius: 8,
      background: "transparent",
      color: dark ? "#fca5a5" : "#dc2626",
      border: `1px solid ${dark ? "rgba(248, 113, 113, 0.35)" : "rgba(220, 38, 38, 0.3)"}`,
      cursor: "pointer", fontSize: 12, fontWeight: 500,
      transition: "background 0.15s",
      "&:hover": {
        background: dark ? "rgba(248, 113, 113, 0.08)" : "rgba(220, 38, 38, 0.06)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
  };
});

// Select single-choice usando o mesmo design do multiSelect já no projeto
// (multiSelectTrigger/Menu/Item). Definido fora do componente principal pra
// não ser recriado a cada render e perder o estado de open/ref.
// A regra nova de acesso por conta (backend: AccessControl/mode.ts).
const ACCESS_CONTROL_MODES = [
  { value: "legacy", label: "Desligada", hint: "Vale a regra de antes: o perfil Admin ou Usuário." },
  { value: "shadow", label: "Observando", hint: "Vale a regra de antes; a nova é calculada junto e as diferenças ficam anotadas." },
  { value: "enforce", label: "Ligada", hint: "Valem os perfis e as exceções de Configurações → Equipe e permissões." },
];
const EMPTY_ACCESS_MODE = { saved: null, server: null, value: null, loading: false };

const SingleSelect = ({ classes, dark, value, onChange, options, placeholder, renderOption, leadingIcon, triggerClassName, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, open, () => setOpen(false));

  const selected = options.find((o) => o.value === value);

  return (
    <div className={classes.multiSelectWrap} ref={ref}>
      <button
        type="button"
        className={`${classes.multiSelectTrigger}${triggerClassName ? ` ${triggerClassName}` : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        aria-label={ariaLabel}
      >
        <span className={classes.multiSelectLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {leadingIcon}
          {selected ? (renderOption ? renderOption(selected) : selected.label) : (placeholder || "Selecione...")}
        </span>
        <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
      </button>
      {open && (
        <div className={classes.multiSelectMenu}>
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={classes.multiSelectItem}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <div style={{ flex: 1, fontSize: 13, color: dark ? "#FAFAFA" : "#09090B", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                  {renderOption ? renderOption(opt) : opt.label}
                </div>
                {isSelected && <Check size={14} style={{ color: dark ? "#5B7CFF" : "#3D5CFD", flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Helpers da listagem de Empresas ──────────────────────────────────
// Métricas de uso da Empresa a partir do groupsUsage (server-side) com
// fallback pro número de contas locais. `ratio` = ocupação (0..1) ou null
// quando não há limite configurado.
const getGroupMetrics = (emp, groupsUsage) => {
  const u = groupsUsage[emp.groupName || ""] || null;
  const usersUsed = u?.total?.used ?? 0;
  const usersLimit = u?.total?.limit ?? null;
  const accountsUsed = u?.contas?.used ?? emp.accounts.length;
  const accountsLimit = u?.contas?.limit ?? null;
  // Limite 0 é teto real (bloqueia tudo), não "sem limite" — igual ao KPI do
  // detalhe e ao backend. ratio >= 1 = cheio.
  const ratio = (used, limit) => (limit == null ? null : limit > 0 ? used / limit : 1);
  const usersRatio = ratio(usersUsed, usersLimit);
  const accountsRatio = ratio(accountsUsed, accountsLimit);
  const candidates = [usersRatio, accountsRatio].filter((r) => r != null);
  return {
    usersUsed, usersLimit, usersRatio,
    accountsUsed, accountsLimit, accountsRatio,
    // Maior ocupação entre os limites configurados (-1 = nenhum limite).
    maxRatio: candidates.length ? Math.max(...candidates) : -1,
  };
};

// Menu ⋮ de ações raras da Empresa — Menu do Material-UI (portal no body,
// click-away/Esc/teclado/flip nativos), com o visual do projeto via
// PaperProps. O wrapper para a propagação de click/keydown porque o card
// pai é role="button" e os eventos do portal sobem pela árvore React.
const GroupMenu = ({ classes, getItems, label }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  // Itens só são construídos com o menu aberto (evita alocar JSX por card a
  // cada render da lista).
  const visible = open ? getItems().filter(Boolean) : [];

  return (
    <div
      className={classes.menuWrap}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={classes.menuTrigger}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-label={label || "Mais ações"}
        title="Mais ações"
      >
        <MoreVertical size={16} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        transitionDuration={{ enter: 160, exit: 120 }}
        PaperProps={{ className: classes.menuDropdown, elevation: 0 }}
        MenuListProps={{ dense: true, disablePadding: true, "aria-label": label || "Mais ações" }}
      >
        {visible.map((it, idx) =>
          it.divider ? (
            <Divider key={`div-${idx}`} className={classes.menuDivider} />
          ) : (
            <MenuItem
              key={it.key || it.label}
              className={`${classes.menuItem}${it.danger ? ` ${classes.menuItemDanger}` : ""}`}
              onClick={() => { close(); it.onClick(); }}
            >
              {it.icon}
              <span>{it.label}</span>
            </MenuItem>
          )
        )}
      </Menu>
    </div>
  );
};

const SORT_OPTIONS = [
  { value: "name", label: "Nome (A–Z)" },
  { value: "nameDesc", label: "Nome (Z–A)" },
  { value: "occupancy", label: "Maior ocupação de limite" },
  { value: "users", label: "Mais usuários" },
];
const VIEW_MODE_STORAGE_KEY = "companies.viewMode";

// Contas de uma Empresa (detalhe): ordem por nome e visão própria (cards ⇄ lista).
const ACCOUNTS_SORT_OPTIONS = [
  { value: "asc", label: "Nome (A–Z)" },
  { value: "desc", label: "Nome (Z–A)" },
];
const ACCOUNTS_VIEW_MODE_STORAGE_KEY = "companies.accountsViewMode";

// Por nome, sem diferença de maiúscula e acento ("Conta 2" antes de "Conta 10").
// Vale para Empresas e Contas.
const nameCollator = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

// Texto das buscas da tela: minúsculo, sem acento e sem espaço nas pontas ("sao " acha "São").
const searchText = (text) => stripAccents(String(text || "").trim().toLowerCase());
const sortAccountsByName = (accounts, dir) => {
  const direction = dir === "desc" ? -1 : 1;
  return [...accounts].sort((a, b) => direction * nameCollator.compare(String(a.name || ""), String(b.name || "")));
};

// Título "Ações" só para leitor de tela (as tabelas não mostram o texto).
const SR_ONLY_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Segmented control cards ⇄ lista (Empresas e Contas). Escondido no mobile por quem usa.
const ViewModeToggle = ({ classes, value, onChange }) => (
  <div className={classes.viewToggle} role="group" aria-label="Modo de visualização">
    <button
      type="button"
      className={`${classes.viewToggleBtn}${value === "cards" ? ` ${classes.viewToggleBtnActive}` : ""}`}
      onClick={() => onChange("cards")}
      aria-pressed={value === "cards"}
      title="Cards"
      aria-label="Ver como cards"
    >
      <LayoutGrid size={16} />
    </button>
    <button
      type="button"
      className={`${classes.viewToggleBtn}${value === "table" ? ` ${classes.viewToggleBtnActive}` : ""}`}
      onClick={() => onChange("table")}
      aria-pressed={value === "table"}
      title="Lista"
      aria-label="Ver como lista"
    >
      <List size={16} />
    </button>
  </div>
);

const CompaniesByGroup = () => {
  const theme = useTheme();
  const classes = useStyles();
  const dark = theme.palette.type === "dark";
  const { user: currentUser, impersonateUser, superSwitchCompany } = useContext(AuthContext);
  const canImpersonate = !!currentUser?.super;
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [switchingGroupKey, setSwitchingGroupKey] = useState(null);
  // Empresa selecionada pro modal de "qual Conta acessar". null = modal fechado.
  const [accountPickerGroup, setAccountPickerGroup] = useState(null);
  // Conta sendo trocada via card "Acessar Conta" — pra desabilitar enquanto roda.
  const [switchingAccountId, setSwitchingAccountId] = useState(null);

  const [view, setView] = useState("list");
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingAccount, setEditingAccount] = useState(null);
  const [savingAccount, setSavingAccount] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);
  // Desativar/reativar Conta (soft-disable, só super). Desativar exige digitar
  // "DESATIVAR EMPRESA" no modal; reativar é confirm simples.
  // Alvo do modal: { kind: "conta", conta } | { kind: "empresa", empresa } | null.
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusConfirmText, setStatusConfirmText] = useState("");
  const [togglingStatus, setTogglingStatus] = useState(false);
  // Chave OpenAI GRÁTIS (teste) da conta — gerida pelo super-admin, invisível em /integrations.
  const [aiKey, setAiKey] = useState("");
  const [aiKeyMeta, setAiKeyMeta] = useState({ hasKey: false, apiKeyMask: null, isActive: false, loading: false });
  // Regra nova de acesso da conta (só super): `saved` é o gravado na conta (null:
  // segue o do servidor), `value` o escolhido no modal.
  const [accessMode, setAccessMode] = useState(EMPTY_ACCESS_MODE);
  // A conta cuja regra está sendo lida: a resposta de outra, aberta antes, é ignorada.
  const accessModeAccountRef = useRef(null);

  const [editingUser, setEditingUser] = useState(null);
  const [userAccountIds, setUserAccountIds] = useState([]);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => { if (!editingUser) setShowPassword(false); }, [editingUser]);

  const [creatingGroup, setCreatingGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  // "Plano e limites" da Empresa aberta no detalhe (modal).
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deletingGroupBusy, setDeletingGroupBusy] = useState(false);

  // ── Pagination ─────────────────────────────────────────────────
  const GROUPS_PER_PAGE = 10;
  const ACCOUNTS_PER_PAGE = 10;
  const USERS_PER_PAGE = 10;
  const [groupsPage, setGroupsPage] = useState(1);
  const [accountsPage, setAccountsPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [accountsSearch, setAccountsSearch] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  // Ordenação da lista de usuários pelo título da coluna (null: a ordem da API).
  const [usersSort, setUsersSort] = useState(null);

  // ── Listagem de Empresas: busca / ordenação / visão (cards ⇄ lista) ──
  // No mobile é sempre cards (tabela não cabe): o mesmo `isMobile` esconde o
  // toggle e força o modo — uma única fonte de verdade pro breakpoint.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [groupsSearch, setGroupsSearch] = useState("");
  const [groupsSort, setGroupsSort] = useState("name");
  const [viewModePref, setViewMode] = useLocalStorage(VIEW_MODE_STORAGE_KEY, "cards");
  const viewMode = isMobile || viewModePref !== "table" ? "cards" : "table";
  // Contas no detalhe da Empresa: ordem por nome e a mesma regra de visão.
  const [accountsSort, setAccountsSort] = useState("asc");
  const [accountsViewModePref, setAccountsViewMode] = useLocalStorage(ACCOUNTS_VIEW_MODE_STORAGE_KEY, "cards");
  const accountsViewMode = isMobile || accountsViewModePref !== "table" ? "cards" : "table";
  // "Ver usuários" no menu ⋮: abre o detalhe e rola até a seção de Usuários.
  const pendingScrollToUsersRef = useRef(false);
  const usersSectionRef = useRef(null);

  // ESC fecha apenas o modal de Conta (Adicionar/Editar). Modal de Usuário
  // não fecha com ESC para evitar perda acidental de dados no formulário maior.
  useEffect(() => {
    if (!editingAccount) return undefined;
    const handler = (e) => {
      if (e.key === "Escape" && !savingAccount) {
        setEditingAccount(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editingAccount, savingAccount]);

  // groupsUsage: { "<groupName>": { contas, interno, atendimento, total } }
  // Cada métrica é `{ used, limit }`. Server-side aggregate, fonte da verdade
  // dos KPI cards. Multi-conta não consome cobrança extra — cada usuário é
  // contado pela sua Conta primária.
  const [groupsUsage, setGroupsUsage] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // group-users (super only): retorna todos os users sem filtrar pela
      // Conta primária do super-admin — diferente de GET /users que filtra
      // via UserCompany. A tabela aqui mostra users de TODAS as Contas do
      // grupo, então precisamos da lista completa.
      const [companiesRes, usersRes, usageRes] = await Promise.all([
        api.get("/companies/list"),
        api.get("/companies/group-users"),
        api.get("/companies/groups-usage").catch(() => ({ data: {} })),
      ]);
      const cs = companiesRes.data;
      const us = usersRes.data;
      setCompanies(Array.isArray(cs) ? cs : (cs?.companies || []));
      setUsers(Array.isArray(us) ? us : (us?.users || []));
      setGroupsUsage(usageRes.data || {});
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);


  // Agrupa Companies por groupName em Empresas. Stats agregados (contas e
  // usuários) vêm do server via groupsUsage — aqui só derivamos contas,
  // isSystem e a data de criação mais antiga (header do detalhe).
  const groups = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => {
      const key = c.groupName || "__sem_grupo__";
      if (!map.has(key)) {
        map.set(key, {
          groupName: c.groupName || null,
          displayName: c.groupName || "Sem grupo",
          accounts: [],
          isSystem: false,
          earliestCreatedAt: null,
        });
      }
      const group = map.get(key);
      group.accounts.push(c);
      // Sistema = grupo que contém a Conta host (id=1).
      if (c.id === 1) group.isSystem = true;
      const created = c.createdAt ? new Date(c.createdAt) : null;
      if (created && (!group.earliestCreatedAt || created < group.earliestCreatedAt)) {
        group.earliestCreatedAt = created;
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      // Sistema sempre no topo (referência Tracking)
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;
      return nameCollator.compare(a.displayName, b.displayName);
    }).map(group => {
      // Estado de desativação agregado do grupo (feature "Desativar Empresa").
      const disabledCount = group.accounts.filter(c => c.status === false).length;
      return {
        ...group,
        disabledAccountsCount: disabledCount,
        allAccountsDisabled: group.accounts.length > 0 && disabledCount === group.accounts.length,
      };
    });
  }, [companies, users]);

  const selectedGroup = useMemo(() => groups.find(e => e.groupName === selectedGroupName) || null, [groups, selectedGroupName]);

  // Busca (nome, instantânea) + ordenação da listagem. "name" preserva a
  // ordem padrão (Sistema no topo + A–Z) e "nameDesc" a inverte, com o Sistema
  // no topo também; as demais ordenam pela métrica (desc) com desempate por nome.
  // Métricas de uso por Empresa, calculadas uma vez (sort, cards e tabela leem daqui).
  const metricsByGroup = useMemo(
    () => new Map(groups.map(e => [e.groupName || "", getGroupMetrics(e, groupsUsage)])),
    [groups, groupsUsage]
  );
  const metricsOf = (emp) => metricsByGroup.get(emp.groupName || "") || getGroupMetrics(emp, groupsUsage);

  const groupsFiltered = useMemo(() => {
    const q = searchText(groupsSearch);
    const list = q ? groups.filter(e => searchText(e.displayName).includes(q)) : groups;
    if (groupsSort === "name") return list;
    if (groupsSort === "nameDesc") {
      return [...list].sort((a, b) => (Number(b.isSystem) - Number(a.isSystem)) || nameCollator.compare(b.displayName, a.displayName));
    }
    const byName = (a, b) => nameCollator.compare(a.displayName, b.displayName);
    const metric = (e) => metricsByGroup.get(e.groupName || "");
    const sorted = [...list];
    if (groupsSort === "occupancy") {
      sorted.sort((a, b) => (metric(b).maxRatio - metric(a).maxRatio) || byName(a, b));
    } else if (groupsSort === "users") {
      sorted.sort((a, b) => (metric(b).usersUsed - metric(a).usersUsed) || byName(a, b));
    }
    return sorted;
  }, [groups, groupsSearch, groupsSort, metricsByGroup]);

  // ── Editar/Excluir Empresa ──────────────────────────────────────────
  // Editar = renome. O renome vai pela rota da Empresa (PUT /company-groups/:id,
  // numa transação); os PUTs por Conta levam o nome novo. A antiga "conta
  // principal de cobrança" saiu daqui na etapa 12 (a cobrança usa os Dados para
  // a cobrança, em Faturamento).
  const handleSaveEditGroup = async () => {
    if (!editingGroup?.name?.trim() || !editingGroup._originalGroupName) return;
    const newName = editingGroup.name.trim();
    // Os limites antigos e a cortesia saíram daqui: o plano da Empresa cuida
    // deles (CompanyPlanModal). Os limites antigos deixaram de valer (29/10/2026).
    setSavingGroup(true);
    try {
      const targetAccounts = companies.filter(c => (c.groupName || null) === editingGroup._originalGroupName);
      // Nome novo: renomeia a Empresa inteira numa transação (o id dela fica e as
      // contas e os canais do Chat Interno mudam juntos). Os PUTs abaixo já
      // levam o nome novo, que então não muda nada no grupo.
      const companyGroupId = targetAccounts.find(c => c.companyGroupId)?.companyGroupId;
      if (companyGroupId && newName !== editingGroup._originalGroupName) {
        await api.put(`/company-groups/${companyGroupId}`, { name: newName });
      }
      await Promise.all(targetAccounts.map(c =>
        api.put(`/companies/${c.id}`, {
          ...c,
          groupName: newName,
        })
      ));
      toast.success("Empresa atualizada");
      if (selectedGroupName === editingGroup._originalGroupName) {
        setSelectedGroupName(newName);
      }
      await fetchData();
      setEditingGroup(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    setDeletingGroupBusy(true);
    try {
      const targetAccounts = companies.filter(c => (c.groupName || null) === deletingGroup.groupName);
      // Exclui todas as Contas do grupo. Cada DELETE tem CASCADE no model
      // (Users, Whatsapps, Tickets, etc).
      await Promise.all(targetAccounts.map(c => api.delete(`/companies/${c.id}`)));
      toast.success("Empresa excluída");
      await fetchData();
      // Volta pra lista
      setSelectedGroupName(null);
      setView("list");
      setDeletingGroup(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingGroupBusy(false);
    }
  };

  // ── Pagination helpers ────────────────────────────────────────────────
  const paginate = (arr, page, size) => {
    const start = (page - 1) * size;
    return arr.slice(start, start + size);
  };
  const totalPages = (total, size) => Math.max(1, Math.ceil(total / size));

  // Reset de página ao mudar filtros/seleção
  useEffect(() => { setAccountsPage(1); setUsersPage(1); }, [selectedGroupName]);
  useEffect(() => { setAccountsPage(1); }, [accountsSearch, accountsSort]);
  useEffect(() => { setUsersPage(1); }, [usersSearch]);
  useEffect(() => { setGroupsPage(1); }, [groups.length, groupsSearch, groupsSort]);

  // Rola até "Usuários" quando o detalhe abriu via "Ver usuários" (menu ⋮).
  useEffect(() => {
    if (view !== "detail" || !pendingScrollToUsersRef.current) return;
    pendingScrollToUsersRef.current = false;
    const raf = requestAnimationFrame(() => {
      if (usersSectionRef.current) {
        usersSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [view, selectedGroupName]);

  // Componente local de paginação (1, 2, 3 ... < >)
  // Paginação padrão do app (a mesma da página Usuários).
  const renderPagination = (current, total, onChange) => (
    <UsersTablePagination current={current} total={total} onChange={onChange} />
  );

  // Conta escolhida no seletor (Empresa com várias contas). Se der certo, a
  // página recarrega; se falhar (o aviso já apareceu), libera os botões de
  // acessar, e o seletor volta a aceitar escolha e a fechar.
  const handlePickAccount = async (account) => {
    setSwitchingGroupKey(accountPickerGroup?.displayName || "");
    const result = await superSwitchCompany(account.id);
    if (result?.error) setSwitchingGroupKey(null);
    return result;
  };

  // ============== CONTA ==============
  const resetAiKeyState = (loading = false) => {
    setAiKey("");
    setAiKeyMeta({ hasKey: false, apiKeyMask: null, isActive: false, loading });
  };

  const handleOpenCreateAccount = () => {
    setEditingAccount({ name: "", _isNew: true });
    resetAiKeyState(false);
    accessModeAccountRef.current = null;
    setAccessMode(EMPTY_ACCESS_MODE);
  };

  const handleOpenEditAccount = (account) => {
    setEditingAccount({ ...account });
    resetAiKeyState(true);
    api
      .get(`/companies/${account.id}/ai-key`)
      .then(({ data }) =>
        setAiKeyMeta({
          hasKey: !!data.hasKey,
          apiKeyMask: data.apiKeyMask || null,
          isActive: !!data.isActive,
          loading: false,
        })
      )
      .catch(() => resetAiKeyState(false));
    accessModeAccountRef.current = account.id;
    setAccessMode({ ...EMPTY_ACCESS_MODE, loading: true });
    api
      .get(`/companies/${account.id}/access-control-mode`)
      .then(({ data }) => {
        if (accessModeAccountRef.current !== account.id) return;
        setAccessMode({ saved: data.mode, server: data.serverMode, value: data.mode || data.serverMode, loading: false });
      })
      .catch(() => {
        if (accessModeAccountRef.current === account.id) setAccessMode(EMPTY_ACCESS_MODE);
      });
  };

  const handleSaveAccount = async () => {
    if (!editingAccount?.name?.trim()) return;
    setSavingAccount(true);
    try {
      let accountId = editingAccount.id;
      // Whitelist explícita (NÃO spread de editingAccount — evita reenviar limites/
      // cortesia/status). CNPJ vai só-dígitos.
      const fiscalFields = {
        razaoSocial: (editingAccount.razaoSocial || "").trim(),
        nomeFantasia: (editingAccount.nomeFantasia || "").trim(),
        cnpj: (editingAccount.cnpj || "").replace(/\D/g, ""),
        phone: (editingAccount.phone || "").trim(),
        email: (editingAccount.email || "").trim(),
      };
      if (accountId) {
        await api.put(`/companies/${accountId}`, {
          name: editingAccount.name,
          ...fiscalFields,
        });
      } else {
        const { data: created } = await api.post("/companies", {
          name: editingAccount.name,
          groupName: selectedGroup?.groupName,
          isInternalChatChannel: true,
          ...fiscalFields,
        });
        accountId = created?.id;
      }
      // Chave de IA gerida pela Sttelar (opcional): só envia quando o super-admin digitou.
      if (accountId && aiKey.trim()) {
        await api.put(`/companies/${accountId}/ai-key`, { apiKey: aiKey.trim() });
      }
      // A regra de acesso só é gravada quando muda o que vale hoje na conta.
      if (editingAccount.id && accessMode.value && accessMode.value !== (accessMode.saved || accessMode.server)) {
        await api.put(`/companies/${accountId}/access-control-mode`, { mode: accessMode.value });
      }
      toast.success(editingAccount.id ? "Conta atualizada" : "Conta criada");
      setEditingAccount(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleRemoveAiKey = async () => {
    if (!editingAccount?.id) return;
    try {
      await api.put(`/companies/${editingAccount.id}/ai-key`, { apiKey: "" });
      resetAiKeyState(false);
      toast.success("Chave de IA removida");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletingAccount) return;
    try {
      await api.delete(`/companies/${deletingAccount.id}`);
      toast.success("Conta excluída");
      setDeletingAccount(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const closeStatusModal = () => {
    setStatusTarget(null);
    setStatusConfirmText("");
  };

  // Desativar/reativar a Empresa inteira (menu ⋮ da listagem): mesmo modal,
  // endpoint de grupo (PUT /companies/group-status). Só é oferecido para
  // Empresas não-Sistema (a host id=1 nunca entra; o backend também pula).
  // Contas do grupo que de fato mudam de status (ignora as já no estado alvo)
  // — só para o texto do modal; o backend refaz o mesmo filtro.
  const groupStatusTargets = (group, willDisable) =>
    group.accounts.filter(c => (willDisable ? c.status !== false : c.status === false));

  const openAccountStatusModal = (account) => {
    setStatusConfirmText("");
    setStatusTarget({ kind: "conta", account });
  };
  const openGroupStatusModal = (group) => {
    setStatusConfirmText("");
    setStatusTarget({ kind: "empresa", group });
  };
  // Nome e direção (desativar/reativar) derivados do alvo.
  const describeStatusTarget = (t) => t.kind === "empresa"
    ? { name: t.group.displayName, willDisable: !t.group.allAccountsDisabled }
    : { name: t.account.name, willDisable: t.account.status !== false };

  const handleToggleAccountStatus = async () => {
    if (!statusTarget) return;
    const { willDisable } = describeStatusTarget(statusTarget);
    setTogglingStatus(true);
    try {
      if (statusTarget.kind === "empresa") {
        // Uma única request: o backend aplica em todas as Contas do grupo
        // dentro de uma transação (ou tudo ou nada).
        await api.put("/companies/group-status", {
          groupName: statusTarget.group.groupName,
          status: !willDisable,
        });
        toast.success(willDisable ? "Empresa desativada" : "Empresa reativada");
        closeStatusModal();
        await fetchData();
      } else {
        await api.put(`/companies/${statusTarget.account.id}/status`, { status: !willDisable });
        toast.success(willDisable ? "Conta desativada" : "Conta reativada");
        closeStatusModal();
        await fetchData();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTogglingStatus(false);
    }
  };

  // ============== USUÁRIO ==============
  const handleOpenCreateUser = () => {
    const groupAccounts = companies.filter(c => (c.groupName || null) === selectedGroupName);
    const defaultAccount = groupAccounts[0];
    setEditingUser({ name: "", email: "", password: "", profile: "user", accessScope: "full", companyId: defaultAccount?.id, _isNew: true });
    setUserAccountIds(defaultAccount ? [defaultAccount.id] : []);
  };

  const handleOpenEditUser = async (u) => {
    setEditingUser({ ...u, password: "" });
    try {
      const { data } = await api.get(`/users/${u.id}/companies`);
      const list = Array.isArray(data) ? data : (data?.companies || []);
      setUserAccountIds(list.map(c => c.id));
    } catch {
      setUserAccountIds(u.companyId ? [u.companyId] : []);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser?.name?.trim()) {
      toast.error("Nome é obrigatório"); return;
    }
    if (!editingUser?.email?.trim()) {
      toast.error("Email é obrigatório"); return;
    }
    if (editingUser._isNew && (!editingUser.password || editingUser.password.length < 6)) {
      toast.error("Senha precisa ter pelo menos 6 caracteres"); return;
    }
    if (userAccountIds.length === 0) {
      toast.error("Selecione pelo menos uma Conta"); return;
    }

    // Conta principal precisa estar nas Contas vinculadas. Antes havia
    // fallback silencioso (`userAccountIds[0]`) que enganava o admin: ele
    // selecionava "World" no select, desmarcava-a no checklist sem perceber,
    // e o save mascarava com outra Conta. Agora bloqueia explicitamente.
    if (!userAccountIds.includes(editingUser.companyId)) {
      toast.error("A Conta principal selecionada não está nas Contas vinculadas.");
      return;
    }
    const primaryAccount = editingUser.companyId;

    setSavingUser(true);
    try {
      if (editingUser.id) {
        const updateData = {
          name: editingUser.name,
          email: editingUser.email.trim(),
          profile: editingUser.profile,
          accessScope: editingUser.accessScope,
          companyId: primaryAccount,
          maxCompanies: Math.max(userAccountIds.length, editingUser.maxCompanies || 1),
        };
        if (editingUser.password) updateData.password = editingUser.password;
        // 1) PUT primeiro — atualiza User.companyId pra primary nova. Sem
        //    isto, o DELETE da antiga primary abaixo era bloqueado pelo
        //    ERR_CANNOT_REMOVE_ACTIVE_COMPANY (bug histórico do silent
        //    discard de companyId no UpdateUserService).
        await api.put(`/users/${editingUser.id}`, updateData);

        const { data: currentData } = await api.get(`/users/${editingUser.id}/companies`);
        const currentList = Array.isArray(currentData) ? currentData : (currentData?.companies || []);
        const currentIds = currentList.map(c => c.id);
        const toAdd = userAccountIds.filter(id => !currentIds.includes(id));
        const toRemove = currentIds.filter(id => !userAccountIds.includes(id));

        // 2) ADD antes de REMOVE — se um POST falhar (ex.: estouro de
        //    maxCompanies / company fora do grupo), o user mantém os
        //    vínculos atuais. Antes era um Promise.all unificado que dava
        //    pra remover acesso e ficar sem o novo no mesmo save.
        if (toAdd.length > 0) {
          await Promise.all(toAdd.map(id =>
            api.post(`/users/${editingUser.id}/companies`, { companyId: id })
          ));
        }
        if (toRemove.length > 0) {
          await Promise.all(toRemove.map(id =>
            api.delete(`/users/${editingUser.id}/companies/${id}`)
          ));
        }
        toast.success("Usuário atualizado");
      } else {
        const { data: newUser } = await api.post("/users", {
          name: editingUser.name,
          email: editingUser.email.trim(),
          password: editingUser.password,
          profile: editingUser.profile,
          accessScope: editingUser.accessScope,
          companyId: primaryAccount,
        });
        const newId = newUser?.id;
        if (newId) {
          const extras = userAccountIds.filter(id => id !== primaryAccount);
          // Garante que o user pode ser linkado em N contas (default é 1)
          if (extras.length > 0) {
            await api.put(`/users/${newId}`, { maxCompanies: userAccountIds.length });
            await Promise.all(extras.map(id => api.post(`/users/${newId}/companies`, { companyId: id })));
          }
        }
        toast.success("Usuário criado");
      }
      setEditingUser(null);
      setUserAccountIds([]);
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingUser(false);
    }
  };

  // Delete agora roda via UserDeleteImpactModal (preview + plano de
  // transferência por company). O modal faz GET delete-impact e DELETE
  // com body { transfers }; aqui só refetchamos a lista no callback.
  const handleUserDeleted = async () => {
    setDeletingUser(null);
    await fetchData();
  };

  const handleResetMfa = async () => {
    if (!editingUser?.id || !editingUser.mfaEnabled) return;
    if (!window.confirm("Tem certeza? O usuário precisará reconfigurar o app no próximo login.")) return;
    setResettingMfa(true);
    try {
      await api.post(`/users/${editingUser.id}/mfa/reset`);
      toast.success("MFA resetado");
      setEditingUser({ ...editingUser, mfaEnabled: false, mfaEnabledAt: null });
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setResettingMfa(false);
    }
  };

  // ============== EMPRESA ==============
  const handleSaveGroup = async () => {
    if (!creatingGroup?.name?.trim()) return;
    try {
      await api.post("/companies", {
        name: creatingGroup.name,
        groupName: creatingGroup.name,
        isInternalChatChannel: true,
      });
      toast.success("Empresa criada");
      setCreatingGroup(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDetail = (group) => {
    setSelectedGroupName(group.groupName);
    setView("detail");
  };

  const handleOpenUsers = (group) => {
    pendingScrollToUsersRef.current = true;
    handleOpenDetail(group);
  };

  // Abre o modal "Editar Empresa" (usado no header do detalhe e no menu ⋮).
  const openEditGroup = (group) => {
    setEditingGroup({
      name: group.displayName,
      _originalGroupName: group.groupName,
    });
  };

  const handleBack = () => {
    setPlanModalOpen(false);
    setView("list");
    setSelectedGroupName(null);
  };

  // ============== RENDER HELPERS ==============
  const renderEditAccountModal = () => {
    if (!editingAccount) return null;
    return (
      <div className={classes.modalBackdrop} onClick={() => setEditingAccount(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>{editingAccount._isNew ? "Adicionar Conta" : "Editar Conta"}</div>
            <button className={classes.iconBtn} onClick={() => setEditingAccount(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Conta</label>
              <input
                className={classes.fieldInput}
                value={editingAccount.name || ""}
                onChange={(e) => setEditingAccount({ ...editingAccount, name: e.target.value })}
                placeholder="Ex: Empresa 1"
                autoFocus
              />
              <div className={classes.fieldHint}>Os limites de pessoas, contas e números vêm do plano da Empresa, no botão "Plano e limites".</div>
            </div>

            {/* Dados Fiscais POR CONTA (cada loja tem CNPJ próprio) */}
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Razão Social</label>
                <input className={classes.fieldInput} value={editingAccount.razaoSocial || ""}
                  onChange={(e) => setEditingAccount({ ...editingAccount, razaoSocial: e.target.value })}
                  placeholder="Empresa LTDA" autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Nome Fantasia</label>
                <input className={classes.fieldInput} value={editingAccount.nomeFantasia || ""}
                  onChange={(e) => setEditingAccount({ ...editingAccount, nomeFantasia: e.target.value })}
                  placeholder="Nome comercial" autoComplete="off" />
              </div>
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>CNPJ</label>
                <input className={classes.fieldInput} value={maskCnpj(editingAccount.cnpj || "")}
                  onChange={(e) => setEditingAccount({ ...editingAccount, cnpj: e.target.value.replace(/\D/g, "").slice(0, 14) })}
                  placeholder="00.000.000/0000-00" autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Telefone</label>
                <input className={classes.fieldInput} value={editingAccount.phone || ""}
                  onChange={(e) => setEditingAccount({ ...editingAccount, phone: e.target.value })}
                  placeholder="(11) 99999-9999" autoComplete="off" />
              </div>
            </div>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>E-mail</label>
              <input type="text" inputMode="email" className={classes.fieldInput} value={editingAccount.email || ""}
                onChange={(e) => setEditingAccount({ ...editingAccount, email: e.target.value })}
                placeholder="contato@empresa.com" autoComplete="off" />
              <div className={classes.fieldHint}>A cobrança da Empresa usa os Dados para a cobrança, em Faturamento.</div>
            </div>

            <div className={classes.field}>
              <label className={classes.fieldLabel}>API Key da OpenAI (gerida pela Sttelar)</label>
              <input
                className={classes.fieldInput}
                type="password"
                autoComplete="new-password"
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
                placeholder={aiKeyMeta.hasKey ? `Chave atual: ${aiKeyMeta.apiKeyMask}` : "sk-..."}
              />
              <div className={classes.fieldHint}>
                Libera a IA desta conta com uma chave da Sttelar, até você removê-la. O cliente a vê em "Integrações", sem poder mexer; a chave própria dele, se cadastrar, vale antes.
                {aiKeyMeta.loading && <> · carregando…</>}
                {!aiKeyMeta.loading && aiKeyMeta.hasKey && (
                  <>
                    {" · "}Atual: {aiKeyMeta.apiKeyMask} ({aiKeyMeta.isActive ? "ativa" : "inativa"}){" "}
                    <button
                      type="button"
                      onClick={handleRemoveAiKey}
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}
                    >
                      Remover
                    </button>
                  </>
                )}
              </div>
            </div>

            {!editingAccount._isNew && (
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Regra de acesso (Equipe e permissões)</label>
                {accessMode.loading || !accessMode.value ? (
                  <div className={classes.fieldHint}>{accessMode.loading ? "carregando…" : "Não foi possível ler a regra desta conta."}</div>
                ) : (
                  <>
                    <SingleSelect
                      classes={classes}
                      dark={dark}
                      value={accessMode.value}
                      onChange={(v) => setAccessMode({ ...accessMode, value: v })}
                      options={ACCESS_CONTROL_MODES}
                      ariaLabel="Regra de acesso"
                    />
                    <div className={classes.fieldHint}>
                      {ACCESS_CONTROL_MODES.find((opt) => opt.value === accessMode.value)?.hint}
                      {!accessMode.saved && " Esta conta ainda segue o padrão do servidor."}
                      {" "}Só o suporte vê esta opção; vale na hora para todos da conta.
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingAccount(null)} disabled={savingAccount}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveAccount} disabled={savingAccount || !editingAccount.name?.trim()}>
              {savingAccount ? "Salvando..." : (editingAccount._isNew ? "Criar" : "Salvar")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal de desativar/reativar Conta. Desativar é destrutivo na prática
  // (bloqueia login, ignora mensagens, mídia purgada após retenção) → exige
  // digitar a frase exata. Reativar é confirm simples.
  const renderStatusModal = () => {
    if (!statusTarget) return null;
    const { name, willDisable } = describeStatusTarget(statusTarget);
    const confirmPhrase = "DESATIVAR EMPRESA";
    const confirmOk = !willDisable || statusConfirmText === confirmPhrase;
    const isGroup = statusTarget.kind === "empresa";
    const groupAccountsCount = isGroup ? groupStatusTargets(statusTarget.group, willDisable).length : 0;
    const subject = isGroup ? "Empresa" : "Conta";
    return (
      <div className={classes.modalBackdrop} onClick={() => !togglingStatus && closeStatusModal()}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>
              {willDisable ? `Desativar "${name}"?` : `Reativar "${name}"?`}
            </div>
            <button className={classes.iconBtn} onClick={() => !togglingStatus && closeStatusModal()} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            {willDisable ? (
              <>
                <div className={classes.fieldHint} style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
                  {isGroup
                    ? <>{groupAccountsCount === 1 ? "A Conta desta Empresa fica bloqueada" : `Todas as ${groupAccountsCount} Contas desta Empresa ficam bloqueadas`}, mas <strong>nenhum dado é excluído</strong> — dá para reativar quando quiser. Enquanto desativada:</>
                    : <>A Conta fica bloqueada, mas <strong>nenhum dado é excluído</strong> — dá para reativar quando quiser. Enquanto desativada:</>}
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    <li>Usuários {isGroup ? "da Empresa" : "da Conta"} não conseguem entrar no sistema.</li>
                    <li>Mensagens de WhatsApp recebidas são ignoradas.</li>
                    <li>As mídias (fotos, áudios, vídeos, documentos) são apagadas definitivamente após o período de retenção; os textos das conversas permanecem.</li>
                  </ul>
                </div>
                <div className={classes.field}>
                  <label className={classes.fieldLabel}>Digite <strong>{confirmPhrase}</strong> para confirmar</label>
                  <input
                    className={classes.fieldInput}
                    value={statusConfirmText}
                    onChange={(e) => setStatusConfirmText(e.target.value)}
                    placeholder={confirmPhrase}
                    autoFocus
                  />
                </div>
              </>
            ) : (
              <div className={classes.fieldHint} style={{ fontSize: 13, lineHeight: 1.6 }}>
                {isGroup && groupAccountsCount > 1
                  ? `Todas as ${groupAccountsCount} Contas desta Empresa são reativadas. `
                  : ""}
                Os usuários voltam a acessar o sistema e as mensagens de WhatsApp voltam a ser recebidas imediatamente.
              </div>
            )}
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={closeStatusModal} disabled={togglingStatus}>Cancelar</button>
            <button
              className={willDisable ? classes.dangerBtn : classes.primaryBtn}
              onClick={handleToggleAccountStatus}
              disabled={togglingStatus || !confirmOk}
            >
              {togglingStatus
                ? (willDisable ? "Desativando..." : "Reativando...")
                : (willDisable ? `Desativar ${subject}` : `Reativar ${subject}`)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditUserModal = () => {
    if (!editingUser) return null;
    const groupAccounts = companies.filter(c => (c.groupName || null) === selectedGroupName);
    return (
      <div className={classes.modalBackdrop} onClick={() => setEditingUser(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>{editingUser._isNew ? "Adicionar Usuário" : "Editar Usuário"}</div>
            <button className={classes.iconBtn} onClick={() => setEditingUser(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Nome</label>
                <input className={classes.fieldInput} value={editingUser.name || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  placeholder="João Silva" autoFocus autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Email</label>
                <input type="text" inputMode="email" className={classes.fieldInput}
                  value={editingUser.email || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="joao@empresa.com" autoComplete="off" />
              </div>
            </div>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>{editingUser._isNew ? "Senha (mín 6 caracteres)" : "Nova senha (deixe vazio para não alterar)"}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className={classes.fieldInput}
                  style={{ paddingRight: 40 }}
                  value={editingUser.password || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  placeholder={editingUser._isNew ? "Obrigatório" : "••••••"}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer",
                    color: dark ? "#A1A1AA" : "#71717A",
                    padding: 6, display: "flex", alignItems: "center", borderRadius: 6,
                  }}
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                </button>
              </div>
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Perfil</label>
                <SingleSelect
                  classes={classes}
                  dark={dark}
                  value={editingUser.profile || "user"}
                  onChange={(v) => setEditingUser({ ...editingUser, profile: v })}
                  options={[
                    { value: "user", label: "Usuário" },
                    { value: "admin", label: "Admin" },
                  ]}
                />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Tipo de acesso</label>
                <SingleSelect
                  classes={classes}
                  dark={dark}
                  value={editingUser.accessScope || "full"}
                  onChange={(v) => setEditingUser({ ...editingUser, accessScope: v })}
                  options={ACCESS_SCOPES}
                  renderOption={(opt) => (
                    <>
                      <opt.Icon size={14} strokeWidth={2} style={{ color: opt.color, flexShrink: 0 }} />
                      <span>{opt.label}</span>
                    </>
                  )}
                />
              </div>
            </div>
            {!editingUser._isNew && (currentUser?.super || currentUser?.profile === "admin") && (
              <div className={classes.mfaCard}>
                <div className={classes.mfaCardLeft}>
                  <div className={`${classes.mfaIconBadge} ${editingUser.mfaEnabled ? classes.mfaIconBadgeOn : classes.mfaIconBadgeOff}`}>
                    {editingUser.mfaEnabled
                      ? <ShieldCheck size={18} strokeWidth={1.75} />
                      : <ShieldOff size={18} strokeWidth={1.75} />}
                  </div>
                  <div className={classes.mfaTextWrap}>
                    <span className={classes.mfaTitle}>Autenticação em duas etapas</span>
                    <span className={`${classes.mfaStatus} ${editingUser.mfaEnabled ? classes.mfaStatusOn : classes.mfaStatusOff}`}>
                      {editingUser.mfaEnabled
                        ? (editingUser.mfaEnabledAt
                            ? `Ativa desde ${new Date(editingUser.mfaEnabledAt).toLocaleDateString()}`
                            : "Ativa")
                        : "Não configurada"}
                    </span>
                  </div>
                </div>
                {editingUser.mfaEnabled && (
                  <button
                    type="button"
                    className={classes.mfaResetBtn}
                    onClick={handleResetMfa}
                    disabled={resettingMfa}
                  >
                    <RotateCcw size={13} strokeWidth={2} />
                    {resettingMfa ? "Resetando..." : "Resetar MFA"}
                  </button>
                )}
              </div>
            )}
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Contas com acesso</label>
              <AccountsPicker
                accounts={groupAccounts}
                selectedIds={userAccountIds}
                primaryId={editingUser.companyId}
                inputClassName={classes.fieldInput}
                onChange={(ids, primaryId) => {
                  setUserAccountIds(ids);
                  setEditingUser({ ...editingUser, companyId: primaryId });
                }}
              />
              <div className={classes.fieldHint}>O usuário pode trocar entre estas contas pelo perfil. A principal é onde ele entra ao fazer login.</div>
            </div>
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingUser(null)} disabled={savingUser}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveUser} disabled={savingUser || !editingUser.name?.trim() || !editingUser.email?.trim()}>
              {savingUser ? "Salvando..." : (editingUser._isNew ? "Criar" : "Salvar")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateGroupModal = () => {
    if (!creatingGroup) return null;
    return (
      <div className={classes.modalBackdrop} onClick={() => setCreatingGroup(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>Criar Empresa</div>
            <button className={classes.iconBtn} onClick={() => setCreatingGroup(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Empresa</label>
              <input className={classes.fieldInput} value={creatingGroup.name || ""}
                onChange={(e) => setCreatingGroup({ ...creatingGroup, name: e.target.value })}
                placeholder="Ex: Up&Help" autoFocus />
              <div className={classes.fieldHint}>Uma Empresa agrupa várias Contas. Será criada uma Conta inicial com o mesmo nome.</div>
            </div>
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setCreatingGroup(null)}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveGroup} disabled={!creatingGroup.name?.trim()}>Criar</button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditGroupModal = () => {
    if (!editingGroup) return null;
    return (
      <div className={classes.modalBackdrop} onClick={() => !savingGroup && setEditingGroup(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>Editar Empresa</div>
            <button className={classes.iconBtn} onClick={() => !savingGroup && setEditingGroup(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Empresa</label>
              <input
                className={classes.fieldInput}
                value={editingGroup.name || ""}
                onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                placeholder="Ex: Up&Help"
                autoFocus
              />
              <div className={classes.fieldHint}>
                O nome é aplicado a todas as Contas vinculadas a essa Empresa.
              </div>
            </div>

            <div className={classes.fieldHint}>
              O plano, os limites e as cortesias ficam no botão "Plano e limites", ao lado de "Editar".
            </div>
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingGroup(null)} disabled={savingGroup}>Cancelar</button>
            <button
              className={classes.primaryBtn}
              onClick={handleSaveEditGroup}
              disabled={savingGroup || !editingGroup.name?.trim()}
            >
              {savingGroup ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============== DETAIL VIEW ==============
  if (view === "detail" && selectedGroup) {
    const groupAccounts = selectedGroup.accounts;
    const groupAccountIds = new Set(groupAccounts.map(c => c.id));
    const groupUsers = users.filter(u => groupAccountIds.has(u.companyId));
    const planGroupId = groupAccounts.length > 0 && groupAccounts.every(c => c.companyGroupId && c.companyGroupId === groupAccounts[0].companyGroupId)
      ? groupAccounts[0].companyGroupId
      : null;
    const createdAtFmt = selectedGroup.earliestCreatedAt
      ? selectedGroup.earliestCreatedAt.toLocaleDateString("pt-BR")
      : null;

    // Busca + ordem + paginação Contas
    const accountsQuery = searchText(accountsSearch);
    const accountsFiltered = sortAccountsByName(
      accountsQuery ? groupAccounts.filter(c => searchText(c.name).includes(accountsQuery)) : groupAccounts,
      accountsSort
    );
    const accountsTotalPages = totalPages(accountsFiltered.length, ACCOUNTS_PER_PAGE);
    const accountsPageSafe = Math.min(accountsPage, accountsTotalPages);
    const accountsPaginated = paginate(accountsFiltered, accountsPageSafe, ACCOUNTS_PER_PAGE);
    // Pré-calcula contagem de users por Conta — evita O(contas × users) ao
    // renderizar a grid (cada Conta fazia users.filter no map).
    const userCountByAccountId = groupUsers.reduce((acc, u) => {
      acc[u.companyId] = (acc[u.companyId] || 0) + 1;
      return acc;
    }, {});

    // Botões de cada Conta: os mesmos nos cards e na lista.
    const renderAccountStatusButton = (account) => {
      if (!canImpersonate || selectedGroup?.isSystem || account.id === 1) return null;
      const accountDisabled = account.status === false;
      return (
        <Tooltip title={accountDisabled ? "Reativar Conta" : "Desativar Conta"} arrow placement="top">
          <button
            type="button"
            className={`${classes.iconBtn}${accountDisabled ? "" : ` ${classes.danger}`}`}
            onClick={() => openAccountStatusModal(account)}
            aria-label={accountDisabled ? `Reativar conta ${account.name}` : `Desativar conta ${account.name}`}
          >
            {accountDisabled ? <RotateCcw size={14} /> : <Ban size={14} />}
          </button>
        </Tooltip>
      );
    };
    const renderAccountAccessButton = (account) => canImpersonate && (
      <Tooltip title={`Acessar Conta "${account.name}"`} arrow placement="top">
        <button
          type="button"
          className={classes.iconBtn}
          onClick={async () => {
            if (switchingAccountId) return;
            setSwitchingAccountId(account.id);
            // Se der certo, a página recarrega; se falhar (o aviso já apareceu), libera os botões.
            const result = await superSwitchCompany(account.id);
            if (result?.error) setSwitchingAccountId(null);
          }}
          disabled={switchingAccountId === account.id}
          aria-label={`Acessar conta ${account.name}`}
        >
          <LogIn size={14} strokeWidth={2} />
        </button>
      </Tooltip>
    );
    const renderAccountEditButton = (account) => (
      <Tooltip title="Editar" arrow placement="top">
        <button type="button" className={classes.iconBtn} onClick={() => handleOpenEditAccount(account)} aria-label={`Editar conta ${account.name}`}>
          <Pencil size={14} />
        </button>
      </Tooltip>
    );
    const renderAccountDeleteButton = (account) => (
      <Tooltip title="Excluir" arrow placement="top">
        <button type="button" className={`${classes.iconBtn} ${classes.danger}`} onClick={() => setDeletingAccount(account)} aria-label={`Excluir conta ${account.name}`}>
          <Trash2 size={14} />
        </button>
      </Tooltip>
    );

    // Busca + paginação Usuários
    const usersQuery = searchText(usersSearch);
    const usersFiltered = usersQuery
      ? groupUsers.filter(u => searchText(u.name).includes(usersQuery) || searchText(u.email).includes(usersQuery))
      : groupUsers;
    const accountNames = new Map(groupAccounts.map(c => [c.id, c.name]));
    const accountNameOf = (u) => accountNames.get(u.companyId) || null;
    const usersSorted = sortUsers(usersFiltered, usersSort, accountNameOf);
    const usersTotalPages = totalPages(usersSorted.length, USERS_PER_PAGE);
    const usersPageSafe = Math.min(usersPage, usersTotalPages);
    const usersPaginated = paginate(usersSorted, usersPageSafe, USERS_PER_PAGE);

    return (
      <div className={classes.root}>
        {/* Header: voltar + nome + criada em + Editar/Excluir */}
        <div className={classes.detailHeaderV2}>
          <button
            className={classes.detailBackBtn}
            onClick={handleBack}
            title="Voltar"
            aria-label="Voltar"
          >
            <ChevronRight size={20} style={{ transform: "rotate(180deg)" }} />
          </button>
          <div className={classes.detailTitleWrap}>
            <div className={classes.detailNameV2}>{selectedGroup.displayName}</div>
            {createdAtFmt && (
              <div className={classes.detailSubV2}>Criada em {createdAtFmt}</div>
            )}
          </div>
          <div className={classes.detailActionsV2}>
            {planGroupId && (
              <button className={classes.detailEditBtn} onClick={() => setPlanModalOpen(true)}>
                Plano e limites
              </button>
            )}
            {/* "Sem grupo" (groupName null) não é editável — o save exige _originalGroupName. */}
            {selectedGroup.groupName && (
              <button
                className={classes.detailEditBtn}
                onClick={() => openEditGroup(selectedGroup)}
              >
                Editar
              </button>
            )}
            {!selectedGroup.isSystem && (
              <button
                className={classes.detailDeleteBtn}
                onClick={() => setDeletingGroup(selectedGroup)}
              >
                Excluir
              </button>
            )}
          </div>
        </div>

        {/* Plano e limites da Empresa (CompanyGroups), aberto pelo botão do cabeçalho.
            O balde "Sem grupo" junta contas de Empresas diferentes: não tem. */}
        {planModalOpen && planGroupId && (
          <CompanyPlanModal
            classes={classes}
            companyGroupId={planGroupId}
            companyName={selectedGroup.displayName}
            people={groupUsers.filter(u => !u.isAI && !u.super)}
            onClose={() => setPlanModalOpen(false)}
            onChanged={fetchData}
          />
        )}

        {/* Seção: Contas */}
        <div className={classes.sectionV2}>
          <div className={classes.sectionHeaderV2}>
            <div className={classes.sectionTitleWrapV2}>
              <span className={classes.sectionTitleV2}>Contas</span>
              <span className={classes.sectionCountV2}>
                {accountsFiltered.length} de {groupAccounts.length}
              </span>
            </div>
            <div className={classes.sectionActionsV2}>
              <button className={classes.primaryBtn} onClick={handleOpenCreateAccount}>
                <Plus size={16} /> Adicionar Conta
              </button>
            </div>
          </div>

          {/* Toolbar: busca · ordem por nome · visão (como na listagem de Empresas) */}
          <div className={classes.toolbar}>
            <div className={`${classes.searchWrap} ${classes.searchWrapInline}`}>
              <span className={classes.searchIcon}>
                <Search size={16} />
              </span>
              <input
                type="search"
                autoComplete="off"
                className={`${classes.searchInput} ${classes.searchInputSm}`}
                placeholder="Buscar conta por nome..."
                value={accountsSearch}
                onChange={(e) => setAccountsSearch(e.target.value)}
                aria-label="Buscar conta por nome"
              />
              {accountsSearch && (
                <button type="button" className={classes.searchClear} onClick={() => setAccountsSearch("")} aria-label="Limpar busca" title="Limpar busca">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className={classes.toolbarSortWrap}>
              <SingleSelect
                classes={classes}
                dark={dark}
                value={accountsSort}
                onChange={setAccountsSort}
                options={ACCOUNTS_SORT_OPTIONS}
                leadingIcon={<ArrowUpDown size={15} />}
                triggerClassName={classes.toolbarSortTrigger}
                ariaLabel="Ordenar contas"
              />
            </div>
            {!isMobile && (
              <ViewModeToggle classes={classes} value={accountsViewMode} onChange={setAccountsViewMode} />
            )}
          </div>

          {accountsFiltered.length === 0 ? (
            <div className={classes.emptyState}>
              {accountsSearch ? "Nenhuma conta encontrada" : "Nenhuma conta nesta empresa"}
            </div>
          ) : (
            <>
              {accountsViewMode === "table" ? (
                <div className={classes.tableWrap}>
                  <table className={`${classes.table} ${classes.tableNarrow}`}>
                    <colgroup>
                      <col />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 200 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className={classes.th}>Conta</th>
                        <th className={classes.th}>Usuários</th>
                        <th className={`${classes.th} ${classes.thRight}`}>
                          <span style={SR_ONLY_STYLE}>Ações</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountsPaginated.map(account => {
                        const accountDisabled = account.status === false;
                        return (
                          <tr key={account.id} className={`${classes.tr} ${classes.trStatic}${accountDisabled ? ` ${classes.trDisabled}` : ""}`}>
                            <td className={classes.td}>
                              <div className={classes.rowGroup}>
                                <div className={`${classes.trackingIconBox} ${classes.trackingIconBoxSm}`} aria-hidden="true">
                                  {accountDisabled ? <Ban size={16} strokeWidth={1.8} /> : initials(account.name)}
                                </div>
                                <div className={classes.rowGroupText}>
                                  <div className={classes.rowGroupName} title={account.name}>{account.name}</div>
                                  {accountDisabled && (
                                    <div className={classes.rowBadges}>
                                      <span className={classes.trackingDisabledBadge}><Ban size={11} /> Desativada</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className={`${classes.td} ${classes.tdNumber}`}>{userCountByAccountId[account.id] || 0}</td>
                            <td className={classes.td}>
                              <div className={classes.tableRowActions}>
                                {renderAccountAccessButton(account)}
                                {renderAccountEditButton(account)}
                                {renderAccountStatusButton(account)}
                                {renderAccountDeleteButton(account)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={classes.accountsGrid}>
                  {accountsPaginated.map(account => {
                    const accountUserCount = userCountByAccountId[account.id] || 0;
                    const accountDisabled = account.status === false;
                    return (
                      <div key={account.id} className={`${classes.accountCard}${accountDisabled ? ` ${classes.accountCardDisabled}` : ""}`}>
                        <div className={classes.accountHeader}>
                          <div className={classes.accountName}>
                            {accountDisabled
                              ? <Ban size={16} color="#EF4444" />
                              : <Building2 size={16} color={dark ? "#5B7CFF" : "#3D5CFD"} />}
                            {account.name}
                            {accountDisabled && <span className={classes.accountDisabledBadge}>Desativada</span>}
                          </div>
                          {renderAccountStatusButton(account)}
                        </div>
                        <div className={classes.accountStats}>
                          <span><Users size={11} style={{ verticalAlign: "middle" }} /> {accountUserCount} {accountUserCount === 1 ? "usuário" : "usuários"}</span>
                        </div>
                        <div className={classes.accountActions}>
                          {renderAccountAccessButton(account)}
                          {renderAccountEditButton(account)}
                          {renderAccountDeleteButton(account)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {renderPagination(accountsPageSafe, accountsTotalPages, setAccountsPage)}
            </>
          )}
        </div>

        {/* Seção: Usuários (tabela densa) */}
        <div className={classes.sectionV2} ref={usersSectionRef} style={{ scrollMarginTop: 16 }}>
          <div className={classes.sectionHeaderV2}>
            <div className={classes.sectionTitleWrapV2}>
              <span className={classes.sectionTitleV2}>Usuários</span>
              <span className={classes.sectionCountV2}>
                {usersFiltered.length} de {groupUsers.length}
              </span>
            </div>
            <div className={classes.sectionActionsV2}>
              <button className={classes.primaryBtn} onClick={handleOpenCreateUser}>
                <Plus size={16} /> Adicionar Usuário
              </button>
            </div>
          </div>

          <div className={classes.searchWrap}>
            <span className={classes.searchIcon}>
              <Search size={16} />
            </span>
            <input
              type="search"
              autoComplete="off"
              className={classes.searchInput}
              placeholder="Buscar usuário por nome ou email..."
              value={usersSearch}
              onChange={(e) => setUsersSearch(e.target.value)}
            />
            {usersSearch && (
              <button type="button" className={classes.searchClear} onClick={() => setUsersSearch("")} aria-label="Limpar busca" title="Limpar busca">
                <X size={14} />
              </button>
            )}
          </div>

          <UsersTable
            users={usersPaginated}
            currentUserId={currentUser?.id}
            getCompanyName={accountNameOf}
            showLastLogin
            sort={usersSort}
            onSortChange={(key) => {
              setUsersSort((current) => nextUsersSort(current, key));
              setUsersPage(1);
            }}
            canImpersonate={canImpersonate}
            impersonatingId={impersonatingId}
            onImpersonate={async (u) => {
              if (impersonatingId) return;
              setImpersonatingId(u.id);
              // Se der certo, a página recarrega; se falhar (o aviso já apareceu), libera os botões.
              const result = await impersonateUser(u.id);
              if (result?.error) setImpersonatingId(null);
            }}
            onEdit={handleOpenEditUser}
            onDelete={(u) => setDeletingUser(u)}
            searchActive={!!usersSearch}
            emptyMessage="Nenhum usuário nesta empresa"
            emptySearchMessage="Nenhum usuário encontrado"
          />
          {renderPagination(usersPageSafe, usersTotalPages, setUsersPage)}
        </div>

        <div className={classes.bottomSpacer} aria-hidden="true" />

        {renderEditAccountModal()}
        {renderStatusModal()}
        {renderEditUserModal()}
        {renderEditGroupModal()}
        <AccountSelectorModal
          open={!!accountPickerGroup}
          group={accountPickerGroup}
          onClose={() => setAccountPickerGroup(null)}
          onSelect={handlePickAccount}
        />
        <ConfirmationModal
          open={!!deletingAccount}
          title={deletingAccount ? `Excluir "${deletingAccount.name}"?` : ""}
          onClose={() => setDeletingAccount(null)}
          onConfirm={handleDeleteAccount}
        >
          Essa ação não pode ser desfeita. Usuários, mensagens e dados ligados a essa Conta podem ser afetados.
        </ConfirmationModal>
        <UserDeleteImpactModal
          open={!!deletingUser}
          userId={deletingUser?.id}
          userName={deletingUser?.name}
          onClose={() => setDeletingUser(null)}
          onDeleted={handleUserDeleted}
        />
        <ConfirmationModal
          open={!!deletingGroup}
          title={deletingGroup ? `Excluir empresa "${deletingGroup.displayName}"?` : ""}
          onClose={() => setDeletingGroup(null)}
          onConfirm={handleDeleteGroup}
          confirmLoading={deletingGroupBusy}
        >
          {deletingGroup && `Todas as ${deletingGroup.accounts.length} contas desta empresa e os dados vinculados (usuários, mensagens, tickets, conexões WhatsApp) serão removidos. Essa ação NÃO pode ser desfeita.`}
        </ConfirmationModal>
      </div>
    );
  }

  // ============== LIST VIEW ==============
  const activeGroupName = currentUser?.company?.groupName || null;
  const isSearching = groupsSearch.trim().length > 0;
  const groupsTotalPages = totalPages(groupsFiltered.length, GROUPS_PER_PAGE);
  const groupsPageSafe = Math.min(groupsPage, groupsTotalPages);
  const groupsPaginated = paginate(groupsFiltered, groupsPageSafe, GROUPS_PER_PAGE);

  // Ações raras da Empresa (menu ⋮) — mesmas nos cards e na tabela.
  const buildMenuItems = (emp) => {
    // Não-Sistema ⇒ nenhuma conta é a host (id=1), então basta ter contas.
    const canToggleStatus = canImpersonate && !emp.isSystem && emp.accounts.length > 0;
    return [
      // "Sem grupo" (groupName null) não é editável — o save exige _originalGroupName.
      !!emp.groupName && { key: "edit", label: "Editar dados", icon: <Pencil size={15} />, onClick: () => openEditGroup(emp) },
      { key: "users", label: "Ver usuários", icon: <Users size={15} />, onClick: () => handleOpenUsers(emp) },
      canToggleStatus && { divider: true },
      canToggleStatus && (emp.allAccountsDisabled
        ? { key: "reactivate", label: "Reativar empresa", icon: <RotateCcw size={15} />, onClick: () => openGroupStatusModal(emp) }
        : { key: "disable", label: "Desativar empresa", icon: <Ban size={15} />, danger: true, onClick: () => openGroupStatusModal(emp) }),
    ];
  };

  // Botão primário "Acessar conta" (ou estado "Conta ativa"/"Trocar conta")
  // + "Gerenciar" — mesma lógica nos cards e na tabela, só muda o tamanho.
  const renderGroupActions = (emp, { compact = false } = {}) => {
    // "Sistema" não tem Acessar Conta (o super já está nela).
    const showAccessAccount = canImpersonate && !emp.isSystem && emp.accounts?.length > 0;
    // Esta Empresa é a que o user está acessando agora?
    // Compara pelo groupName atual do user (vem do JWT/SerializeUser).
    const isActiveGroup = showAccessAccount && activeGroupName === emp.groupName;
    const activeAccountId = isActiveGroup ? currentUser?.companyId : null;
    const activeAccount = activeAccountId ? emp.accounts.find(c => c.id === activeAccountId) : null;
    const canSwitchAccountHere = isActiveGroup && emp.accounts.length > 1;
    const sm = compact ? ` ${classes.btnSm}` : "";
    // Compacto (tabela): devolve só os botões — a célula já tem o wrapper flex
    // (junto com o menu ⋮). Cards: grid de rodapé.
    const buttons = (
      <>
        {showAccessAccount && (isActiveGroup ? (
          // Estado "Conta ativa": feedback verde + nome da Conta atual.
          // Multi-conta vira clicável (abre seletor pra trocar).
          <button
            type="button"
            className={`${classes.trackingActiveBtn}${sm} ${canSwitchAccountHere ? classes.trackingActiveBtnClickable : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!canSwitchAccountHere) return;
              setAccountPickerGroup(emp);
            }}
            aria-label={
              canSwitchAccountHere
                ? `Trocar conta (atual: ${activeAccount?.name || ""})`
                : `Você está acessando ${activeAccount?.name || emp.displayName}`
            }
            title={
              canSwitchAccountHere
                ? `Conta atual: ${activeAccount?.name || "—"} · clique para trocar`
                : "Você está acessando esta conta"
            }
            tabIndex={canSwitchAccountHere ? 0 : -1}
          >
            <span className={classes.trackingActiveDot} aria-hidden="true" />
            <Check size={14} strokeWidth={2.5} />
            <span className={classes.trackingBtnLabel}>
              {canSwitchAccountHere ? "Trocar conta" : "Conta ativa"}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className={`${classes.trackingAccessBtn}${sm}`}
            onClick={async (e) => {
              e.stopPropagation();
              if (switchingGroupKey) return;
              // 1 conta → entra direto. N contas → abre seletor.
              if (emp.accounts.length === 1) {
                setSwitchingGroupKey(emp.displayName);
                // Se der certo, a página recarrega; se falhar (o aviso já apareceu), libera os botões.
                const result = await superSwitchCompany(emp.accounts[0].id);
                if (result?.error) setSwitchingGroupKey(null);
              } else {
                setAccountPickerGroup(emp);
              }
            }}
            disabled={switchingGroupKey === emp.displayName}
            aria-label={`Acessar conta de ${emp.displayName}`}
            title={emp.accounts.length > 1
              ? `Escolher 1 entre ${emp.accounts.length} contas`
              : "Entrar no contexto da conta"}
          >
            <ArrowRight size={14} strokeWidth={2} />
            <span className={classes.trackingBtnLabel}>
              {switchingGroupKey === emp.displayName ? "Acessando..." : (compact ? "Acessar" : "Acessar conta")}
            </span>
          </button>
        ))}
        <button
          type="button"
          className={`${classes.trackingManageBtn}${sm}`}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDetail(emp);
          }}
          aria-label={`Gerenciar empresa ${emp.displayName}`}
          title="Administrar contas, usuários e limites desta Empresa"
        >
          <Settings size={14} strokeWidth={2} />
          <span className={classes.trackingBtnLabel}>Gerenciar</span>
        </button>
      </>
    );
    if (compact) return buttons;
    return (
      <div className={showAccessAccount ? classes.trackingActionsRow : classes.trackingActionsRowSingle}>
        {buttons}
      </div>
    );
  };

  const renderGroupBadges = (emp) => (
    <>
      {emp.isSystem && <span className={classes.trackingSystemBadge}>Sistema</span>}
      {emp.allAccountsDisabled ? (
        <span className={classes.trackingDisabledBadge}><Ban size={11} /> Desativada</span>
      ) : emp.disabledAccountsCount > 0 && (
        <span className={classes.trackingDisabledBadge}>
          <Ban size={11} /> {emp.disabledAccountsCount} de {emp.accounts.length} contas desativadas
        </span>
      )}
    </>
  );

  const renderGroupCard = (emp) => {
    const m = metricsOf(emp);
    return (
      <div
        key={emp.displayName}
        className={`${classes.trackingCard}${emp.allAccountsDisabled ? ` ${classes.trackingCardDisabled}` : ""}`}
        onClick={() => handleOpenDetail(emp)}
        role="button"
        tabIndex={0}
        // Só o Enter no próprio card — o dos botões internos já tem ação própria.
        onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) handleOpenDetail(emp); }}
        aria-label={`Empresa ${emp.displayName}`}
      >
        {/* Topo: inicial + nome + badges + menu ⋮ */}
        <div className={classes.trackingCardHeader}>
          <div className={classes.trackingIconBox} aria-hidden="true">
            {emp.allAccountsDisabled ? <Ban size={20} strokeWidth={1.8} /> : initials(emp.displayName)}
          </div>
          <div className={classes.trackingCardTitle}>
            <div className={classes.trackingCardName} title={emp.displayName}>{emp.displayName}</div>
            <div className={classes.trackingBadges}>{renderGroupBadges(emp)}</div>
          </div>
          <GroupMenu classes={classes} getItems={() => buildMenuItems(emp)} label={`Mais ações de ${emp.displayName}`} />
        </div>

        {/* Meio: medidores de limite */}
        <div className={classes.meterList}>
          <LimitMeter classes={classes} label="Usuários" used={m.usersUsed} limit={m.usersLimit} />
          <LimitMeter classes={classes} label="Contas" used={m.accountsUsed} limit={m.accountsLimit} />
        </div>

        {/* Rodapé: Acessar conta (primário) + Gerenciar (outline) */}
        {renderGroupActions(emp)}
      </div>
    );
  };

  const renderGroupsTable = () => (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <colgroup>
          <col />
          <col style={{ width: 170 }} />
          <col style={{ width: 170 }} />
          <col style={{ width: 280 }} />
        </colgroup>
        <thead>
          <tr>
            <th className={classes.th}>Empresa</th>
            <th className={classes.th}>Usuários</th>
            <th className={classes.th}>Contas</th>
            <th className={`${classes.th} ${classes.thRight}`}>
              <span style={SR_ONLY_STYLE}>Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groupsPaginated.map(emp => {
            const m = metricsOf(emp);
            return (
              <tr
                key={emp.displayName}
                className={`${classes.tr}${emp.allAccountsDisabled ? ` ${classes.trDisabled}` : ""}`}
                onClick={() => handleOpenDetail(emp)}
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) handleOpenDetail(emp); }}
              >
                <td className={classes.td}>
                  <div className={classes.rowGroup}>
                    <div className={`${classes.trackingIconBox} ${classes.trackingIconBoxSm}`} aria-hidden="true">
                      {emp.allAccountsDisabled ? <Ban size={16} strokeWidth={1.8} /> : initials(emp.displayName)}
                    </div>
                    <div className={classes.rowGroupText}>
                      <div className={classes.rowGroupName} title={emp.displayName}>{emp.displayName}</div>
                      <div className={classes.rowBadges}>{renderGroupBadges(emp)}</div>
                    </div>
                  </div>
                </td>
                <td className={classes.td}>
                  <LimitMeter classes={classes} label="Usuários" used={m.usersUsed} limit={m.usersLimit} small hideLabel />
                </td>
                <td className={classes.td}>
                  <LimitMeter classes={classes} label="Contas" used={m.accountsUsed} limit={m.accountsLimit} small hideLabel />
                </td>
                <td className={classes.td}>
                  <div className={classes.tableRowActions}>
                    {renderGroupActions(emp, { compact: true })}
                    <GroupMenu classes={classes} getItems={() => buildMenuItems(emp)} label={`Mais ações de ${emp.displayName}`} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.listHeader}>
        <div>
          <div className={classes.listTitle}>Empresas</div>
          <div className={classes.listSub}>{groups.length} {groups.length === 1 ? "empresa cadastrada" : "empresas cadastradas"}</div>
        </div>
        <button className={classes.primaryBtn} onClick={() => setCreatingGroup({ name: "" })}>
          <Plus size={16} /> Criar Empresa
        </button>
      </div>

      {loading ? (
        <div className={classes.cardsGrid} aria-busy="true" aria-live="polite">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={classes.skeletonCard}>
              <div className={classes.skeletonRow}>
                <div className={classes.skeletonBlock} style={{ width: 40, height: 40, borderRadius: 10 }} />
                <div style={{ flex: 1 }}>
                  <div className={classes.skeletonBlock} style={{ width: "60%", height: 14, marginBottom: 8 }} />
                  <div className={classes.skeletonBlock} style={{ width: "40%", height: 11 }} />
                </div>
              </div>
              <div className={classes.skeletonFooter}>
                <div>
                  <div className={classes.skeletonBlock} style={{ width: 32, height: 18, margin: "0 auto 6px" }} />
                  <div className={classes.skeletonBlock} style={{ width: 48, height: 9, margin: "0 auto" }} />
                </div>
                <div>
                  <div className={classes.skeletonBlock} style={{ width: 32, height: 18, margin: "0 auto 6px" }} />
                  <div className={classes.skeletonBlock} style={{ width: 48, height: 9, margin: "0 auto" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className={classes.emptyHero}>
          <div className={classes.emptyHeroIcon}>
            <Building2 size={32} strokeWidth={1.6} />
          </div>
          <div className={classes.emptyHeroTitle}>Nenhuma empresa cadastrada</div>
          <div className={classes.emptyHeroSub}>
            Crie a primeira empresa para começar a organizar contas e usuários do seu grupo. Você poderá adicionar quantas contas precisar e gerenciar permissões individualmente.
          </div>
          <button
            className={`${classes.primaryBtn} ${classes.emptyHeroCta}`}
            onClick={() => setCreatingGroup({ name: "" })}
          >
            <Plus size={16} /> Criar primeira empresa
          </button>
        </div>
      ) : (
        <>
          {/* Toolbar: busca · ordenação · visão */}
          <div className={classes.toolbar}>
            <div className={`${classes.searchWrap} ${classes.searchWrapInline}`}>
              <span className={classes.searchIcon}><Search size={16} /></span>
              <input
                type="search"
                autoComplete="off"
                className={`${classes.searchInput} ${classes.searchInputSm}`}
                placeholder="Buscar empresa por nome..."
                value={groupsSearch}
                onChange={(e) => setGroupsSearch(e.target.value)}
                aria-label="Buscar empresa por nome"
              />
              {isSearching && (
                <button
                  type="button"
                  className={classes.searchClear}
                  onClick={() => setGroupsSearch("")}
                  aria-label="Limpar busca"
                  title="Limpar busca"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className={classes.toolbarSortWrap}>
              <SingleSelect
                classes={classes}
                dark={dark}
                value={groupsSort}
                onChange={setGroupsSort}
                options={SORT_OPTIONS}
                leadingIcon={<ArrowUpDown size={15} />}
                triggerClassName={classes.toolbarSortTrigger}
                ariaLabel="Ordenar empresas"
              />
            </div>
            {!isMobile && (
              <ViewModeToggle classes={classes} value={viewMode} onChange={setViewMode} />
            )}
          </div>

          {isSearching && groupsFiltered.length > 0 && (
            <div className={classes.resultsLine} aria-live="polite">
              Mostrando <strong>{groupsFiltered.length}</strong> de <strong>{groups.length}</strong> {groups.length === 1 ? "empresa" : "empresas"}
            </div>
          )}

          {groupsFiltered.length === 0 ? (
            <div className={classes.emptySearch} role="status">
              <div className={classes.emptySearchIcon}><Search size={22} /></div>
              <div className={classes.emptySearchTitle}>Nenhuma empresa encontrada para “{groupsSearch.trim()}”</div>
              <div className={classes.emptySearchSub}>Confira a grafia ou tente um termo mais curto.</div>
              <button
                type="button"
                className={`${classes.ghostBtn} ${classes.emptySearchBtn}`}
                onClick={() => setGroupsSearch("")}
              >
                <X size={14} /> Limpar busca
              </button>
            </div>
          ) : viewMode === "table" ? (
            renderGroupsTable()
          ) : (
            <div className={classes.trackingGrid}>
              {groupsPaginated.map(renderGroupCard)}
            </div>
          )}

          {groupsFiltered.length > 0 && renderPagination(groupsPageSafe, groupsTotalPages, setGroupsPage)}
        </>
      )}

      <div className={classes.bottomSpacer} aria-hidden="true" />

      {renderCreateGroupModal()}
      {/* Editar dados / Desativar empresa também acionáveis pelo menu ⋮ da lista. */}
      {renderEditGroupModal()}
      {renderStatusModal()}

      {/* Modal de seleção de Conta — também precisa estar disponível na LISTA
          de Empresas (não só no detalhe), porque é onde o botão "Acessar Conta"
          fica. Sem isso, clicar no botão setava o estado mas o componente
          não estava no DOM e nada aparecia. */}
      <AccountSelectorModal
        open={!!accountPickerGroup}
        group={accountPickerGroup}
        onClose={() => setAccountPickerGroup(null)}
        onSelect={handlePickAccount}
      />
    </div>
  );
};

export default CompaniesByGroup;
